import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  ArrowLeft, Loader2, RefreshCw, AlertTriangle, Clock, Users,
  CheckCircle2, XCircle, Calendar, ExternalLink, Filter, Info,
  TrendingUp, BarChart3, Target, ChevronDown, ChevronUp, Zap,
  Activity, AlertOctagon, Bell, Skull, Play, Database, Code,
  Search, Eye, Copy, Check, Download, UserX, AlertCircle, Shuffle, Crown, HelpCircle,
  Flame, Briefcase, Grid3X3, Settings, X, Plus
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis, Legend,
  PieChart, Pie, ReferenceLine
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { WorkloadHelpButton } from "./WorkloadHelpButton";
import { TeamConfigView } from "./TeamConfigView";


const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Colors for urgency status
const URGENCY_COLORS = {
  "Vencida": "#ef4444",
  "Sin fecha": "#94a3b8",
  "Próximos 7 días": "#f59e0b",
  "Próximos 8-30 días": "#22c55e",
  "Más de 30 días": "#3b82f6"
};

const STATUS_COLORS = [
  "#8b5cf6", "#06b6d4", "#f59e0b", "#22c55e", "#ef4444", "#ec4899", "#6366f1"
];

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card className={`border-l-4 ${color}`}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full bg-slate-100`}>
          <Icon className="h-6 w-6 text-slate-600" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`mt-2 flex items-center text-xs ${trend > 0 ? 'text-red-500' : 'text-green-500'}`}>
          {trend > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1 rotate-180" />}
          {Math.abs(trend)}% vs semana anterior
        </div>
      )}
    </CardContent>
  </Card>
);

export const WorkloadAnalysisView = ({ onBack }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserTasks, setShowUserTasks] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [excludeWaitingStatuses, setExcludeWaitingStatuses] = useState(true);
  const [activeTab, setActiveTab] = useState("current-sprint");
  const [sprintUserDetail, setSprintUserDetail] = useState(null);
  const [showSprintUserDetail, setShowSprintUserDetail] = useState(false);
  
  // Team filtering state
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("all");
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [showTeamConfig, setShowTeamConfig] = useState(false);

  // Activity tracking (with duplicate prevention)
  const hasTrackedView = React.useRef(false);
  const trackActivity = useCallback(async (action, details = null) => {
    try {
      await axios.post(`${API}/track-activity`, {
        action,
        module: "workload",
        details
      }, { withCredentials: true });
    } catch (e) { /* silent */ }
  }, []);

  // Track view on mount (only once)
  useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackActivity("view");
    }
  }, [trackActivity]);
  
  // Load teams on mount
  useEffect(() => {
    loadTeams();
  }, []);
  
  const loadTeams = async () => {
    setTeamsLoading(true);
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error("Error loading teams:", error);
    } finally {
      setTeamsLoading(false);
    }
  };
  
  // Get current team members (from selected team - REQUIRED)
  const currentTeamMembers = useMemo(() => {
    if (selectedTeamId && selectedTeamId !== "all") {
      const selectedTeam = teams.find(t => t.id === selectedTeamId);
      if (selectedTeam && selectedTeam.members && selectedTeam.members.length > 0) {
        return selectedTeam.members.map(m => ({
          username: m.username,
          email: m.email,
          specialty: m.specialty || "General",
          role_type: m.specialty || "Team"
        }));
      }
    }
    // Return empty array if no team selected - will trigger the "create team first" screen
    return [];
  }, [selectedTeamId, teams]);
  
  // Check if user needs to create a team first
  const needsTeamCreation = teams.length === 0 || (selectedTeamId === "all" && teams.length > 0);
  
  const [dhiDiagnostic, setDhiDiagnostic] = useState(null);
  const [showDhiModal, setShowDhiModal] = useState(false);
  const [dhiModalTab, setDhiModalTab] = useState("overdue");
  const [showIndicatorsHelp, setShowIndicatorsHelp] = useState(false);
  
  // Raw Data tab states
  const [rawDataSearch, setRawDataSearch] = useState("");
  const [rawDataStatus, setRawDataStatus] = useState("all");
  const [rawDataUrgency, setRawDataUrgency] = useState("all");
  const [showQueryCode, setShowQueryCode] = useState(false);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [copiedQuery, setCopiedQuery] = useState(false);
  const [leadTimeDays, setLeadTimeDays] = useState(0); // For capacity projection (0-3 days)
  const [selectedCapacityCell, setSelectedCapacityCell] = useState(null); // For capacity drill-down

  // Statuses to exclude (waiting/review statuses)
  const WAITING_STATUSES = [
    "ready for review",
    "waiting for feedback", 
    "waiting for review",
    "send ok to enovia"
  ];
  
  // Active execution statuses for Current Sprint
  const EXECUTION_STATUSES = [
    "to do",
    "in progress", 
    "blocked",
    "revision",
    "draft",
    "planning"
  ];

  useEffect(() => {
    // Only fetch data if a valid team is selected
    if (selectedTeamId && selectedTeamId !== "all") {
      fetchWorkloadData(false);
    }
  }, [selectedTeamId]);

  const fetchWorkloadData = async (forceRefresh = false) => {
    // Don't fetch if no team is selected
    if (!selectedTeamId || selectedTeamId === "all") {
      return;
    }
    
    try {
      setIsLoading(true);
      let url = forceRefresh 
        ? `${API}/workload/analyze?force_refresh=true&team_id=${selectedTeamId}`
        : `${API}/workload/analyze?team_id=${selectedTeamId}`;
      
      const response = await axios.get(url);
      setData(response.data);
      setCacheInfo({
        cached_at: response.data.cached_at,
        is_cached: response.data.is_cached,
        last_clickup_sync: response.data.last_clickup_sync,
        data_source: response.data.data_source || "lakebase"
      });
      
      const teamName = selectedTeamId !== "all" 
        ? teams.find(t => t.id === selectedTeamId)?.name 
        : null;
      
      if (response.data.is_cached) {
        toast.info(teamName ? `Datos de ${teamName} desde caché` : "Datos desde caché", {
          description: `${response.data.total_tasks} tareas - Actualizado: ${formatCacheDate(response.data.cached_at)}`
        });
      } else {
        toast.success(teamName ? `Datos de ${teamName} actualizados` : "Datos actualizados", {
          description: `${response.data.total_tasks} tareas activas analizadas`
        });
      }
    } catch (error) {
      console.error("Error fetching workload:", error);
      toast.error("Error al cargar datos", {
        description: error.response?.data?.detail || "No se pudo conectar con ClickUp"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCacheDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatSyncDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return null;
    }
  };

  // Filter tasks based on selections (for Pipeline Governance tab)
  const filteredTasks = useMemo(() => {
    if (!data) return [];
    const seen = new Set();
    return data.tasks.filter(task => {
      // Deduplicate by task_id
      if (task.task_id && seen.has(task.task_id)) return false;
      if (task.task_id) seen.add(task.task_id);
      
      // Exclude waiting/review statuses if filter is active
      if (excludeWaitingStatuses) {
        const statusLower = (task.status_status || "").toLowerCase();
        if (WAITING_STATUSES.some(ws => statusLower.includes(ws))) return false;
      }
      return true;
    });
  }, [data, excludeWaitingStatuses, WAITING_STATUSES]);

  // =============================================
  // CURRENT SPRINT DATA (Tactical View)
  // =============================================
  
  // Sprint tasks: forcefully exclude waiting statuses
  const sprintTasks = useMemo(() => {
    if (!data) return [];
    const seen = new Set();
    return data.tasks.filter(task => {
      // Deduplicate by task_id
      if (task.task_id && seen.has(task.task_id)) return false;
      if (task.task_id) seen.add(task.task_id);
      
      const statusLower = (task.status_status || "").toLowerCase();
      // Always exclude waiting/review statuses
      if (WAITING_STATUSES.some(ws => statusLower.includes(ws))) return false;
      return true;
    });
  }, [data, WAITING_STATUSES]);

  // Sprint KPIs
  const sprintKPIs = useMemo(() => {
    const wip = sprintTasks.length;
    const overdueCount = sprintTasks.filter(t => t.urgency_status === "Vencida").length;
    const noDateCount = sprintTasks.filter(t => t.urgency_status === "Sin fecha").length;
    const planningHygiene = wip > 0 ? Math.round((noDateCount / wip) * 100) : 0;
    
    // Zombies: in progress for > 30 days
    const zombies = sprintTasks.filter(t => {
      const statusLower = (t.status_status || "").toLowerCase();
      return statusLower.includes("in progress") && (t.task_age_days || 0) > 30;
    });
    
    return {
      wip,
      overdueCount,
      planningHygiene,
      noDateCount,
      zombieCount: zombies.length,
      zombieTasks: zombies.sort((a, b) => (b.task_age_days || 0) - (a.task_age_days || 0))
    };
  }, [sprintTasks]);

  // Capacity Projection Timeline (Gantt Inverso)
  const capacityProjection = useMemo(() => {
    if (!sprintTasks || sprintTasks.length === 0) return { users: [], conflicts: [], days: [] };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate days: 7 days before + today + 14 days after = 22 days total
    const days = [];
    const todayStr = today.toISOString().split('T')[0];
    let todayIndex = 0;
    
    for (let i = -7; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      if (i === 0) todayIndex = days.length;
      
      days.push({
        date,
        dateStr,
        dayLabel: date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: i === 0,
        isPast: i < 0
      });
    }
    
    // Filter tasks with due dates within our range (7 days ago to 14 days ahead)
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - 7);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 14);
    
    const tasksWithDueDate = sprintTasks.filter(t => {
      if (!t.due_date) return false;
      const dueDate = new Date(t.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate >= minDate && dueDate <= maxDate;
    });
    
    // Group by user and calculate projections
    const userProjections = {};
    const minDateMs = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).getTime();
    
    tasksWithDueDate.forEach(task => {
      const username = task.username;
      if (!username) return;
      
      if (!userProjections[username]) {
        userProjections[username] = {
          username,
          shortName: username.split(' ')[0],
          tasks: [],
          dayLoad: {} // Map of dateStr -> task count
        };
      }
      
      const dueDate = new Date(task.due_date);
      dueDate.setHours(0, 0, 0, 0);
      
      // Calculate start date (due_date - leadTimeDays, skipping weekends)
      const startDate = new Date(dueDate);
      let daysToSubtract = leadTimeDays;
      while (daysToSubtract > 0) {
        startDate.setDate(startDate.getDate() - 1);
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (startDate.getDay() !== 0 && startDate.getDay() !== 6) {
          daysToSubtract--;
        }
      }
      
      // Don't go before our date range (7 days ago)
      if (startDate.getTime() < minDateMs) {
        startDate.setTime(minDateMs);
      }
      
      // Calculate day indices relative to our range (index 7 = today)
      const startDayIndex = Math.max(0, Math.floor((startDate.getTime() - minDateMs) / (1000 * 60 * 60 * 24)));
      const endDayIndex = Math.min(21, Math.floor((dueDate.getTime() - minDateMs) / (1000 * 60 * 60 * 24)));
      
      const taskProjection = {
        ...task,
        startDate,
        endDate: dueDate,
        startDayIndex,
        endDayIndex,
        barWidth: Math.max(1, endDayIndex - startDayIndex + 1)
      };
      
      userProjections[username].tasks.push(taskProjection);
      
      // Track load per day for conflict detection (store full task object)
      for (let d = startDayIndex; d <= endDayIndex && d <= 21; d++) {
        const dayStr = days[d]?.dateStr;
        if (dayStr) {
          if (!userProjections[username].dayLoad[dayStr]) {
            userProjections[username].dayLoad[dayStr] = [];
          }
          userProjections[username].dayLoad[dayStr].push({
            name: task.task_name || task.task_id,
            status: task.status_status || 'Sin estado',
            url: task.url,
            isInProgress: (task.status_status || '').toLowerCase().includes('in progress')
          });
        }
      }
    });
    
    // Convert to array and detect conflicts
    const users = Object.values(userProjections)
      .filter(u => u.tasks.length > 0)
      .sort((a, b) => b.tasks.length - a.tasks.length);
    
    // Detect conflicts (days with 2+ tasks overlapping)
    const conflicts = [];
    users.forEach(user => {
      Object.entries(user.dayLoad).forEach(([dateStr, tasks]) => {
        if (tasks.length >= 2) {
          conflicts.push({
            username: user.username,
            shortName: user.shortName,
            date: dateStr,
            taskCount: tasks.length,
            taskNames: tasks.slice(0, 3),
            severity: tasks.length >= 3 ? 'critical' : 'warning'
          });
        }
      });
    });
    
    return { users, conflicts, days };
  }, [sprintTasks, leadTimeDays]);

  // Sprint user workloads (for execution load chart)
  const sprintUserWorkloads = useMemo(() => {
    // Start with all current team members (even those with 0 tasks)
    const userMap = {};
    currentTeamMembers.forEach(member => {
      userMap[member.username] = {
        username: member.username,
        email: member.email,
        specialty: member.specialty,
        role_type: member.role_type,
        total: 0,
        overdue: 0,
        noDate: 0,
        onTime: 0,
        // New: Focus dispersion tracking
        spaces: new Set(),
        // New: Priority inversion tracking
        inProgressLowPriority: [],
        pendingHighPriority: [],
        // New: Stale tasks tracking
        staleTasks: [],
        // New: Critical tasks tracking (Hero Syndrome)
        criticalTasks: []
      };
    });
    
    // Track total critical tasks for Hero Syndrome calculation
    let totalCriticalTasks = 0;
    
    // Count tasks for users who have them
    sprintTasks.forEach(task => {
      if (userMap[task.username]) {
        const user = userMap[task.username];
        user.total++;
        
        // Track unique spaces (Focus Dispersion)
        if (task.space_name) {
          user.spaces.add(task.space_name);
        }
        
        const statusLower = (task.status_status || "").toLowerCase();
        const priorityLower = (task.priority || "normal").toLowerCase();
        
        // Track critical tasks (Urgent or High priority) - Hero Syndrome
        if (priorityLower === "urgent" || priorityLower === "high") {
          user.criticalTasks.push(task);
          totalCriticalTasks++;
        }
        
        // Track stale tasks (in progress without update > 3 days)
        if (statusLower.includes("in progress") && task.updated_date) {
          const updateDate = new Date(task.updated_date);
          const now = new Date();
          const daysSinceUpdate = Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
          if (daysSinceUpdate > 3) {
            user.staleTasks.push({ ...task, daysSinceUpdate });
          }
        }
        
        // Track priority inversion (Cherry Picking)
        if (statusLower.includes("in progress")) {
          if (priorityLower === "normal" || priorityLower === "low") {
            user.inProgressLowPriority.push(task);
          }
        } else if (statusLower === "to do" || statusLower === "open") {
          if (priorityLower === "urgent" || priorityLower === "high") {
            user.pendingHighPriority.push(task);
          }
        }
        
        if (task.urgency_status === "Vencida") {
          user.overdue++;
        } else if (task.urgency_status === "Sin fecha") {
          user.noDate++;
        } else {
          user.onTime++;
        }
      }
    });
    
    // Find the user with the highest critical percentage (Hero)
    const usersWithCritical = Object.values(userMap)
      .map(u => ({
        username: u.username,
        criticalCount: u.criticalTasks.length,
        criticalPercentage: totalCriticalTasks > 0 
          ? Math.round((u.criticalTasks.length / totalCriticalTasks) * 100) 
          : 0
      }))
      .filter(u => u.criticalCount > 0)
      .sort((a, b) => b.criticalPercentage - a.criticalPercentage);
    
    const topHeroUsername = usersWithCritical.length > 0 ? usersWithCritical[0].username : null;
    
    return Object.values(userMap)
      .map(u => {
        const criticalCount = u.criticalTasks.length;
        const criticalPercentage = totalCriticalTasks > 0 
          ? Math.round((criticalCount / totalCriticalTasks) * 100) 
          : 0;
        // Only the top user with critical tasks gets the hero badge
        const isHero = u.username === topHeroUsername && criticalCount > 0;
        
        return {
          name: u.username.split(" ")[0],
          fullName: u.username,
          email: u.email,
          specialty: u.specialty,
          role_type: u.role_type,
          "Vencida": u.overdue,
          "Sin fecha": u.noDate,
          "A tiempo": u.onTime,
          total: u.total,
          // New metrics
          spaceCount: u.spaces.size,
          spaces: Array.from(u.spaces),
          hasPriorityInversion: u.inProgressLowPriority.length > 0 && u.pendingHighPriority.length > 0,
          inProgressLowPriority: u.inProgressLowPriority,
          pendingHighPriority: u.pendingHighPriority,
          // Stale metrics
          staleCount: u.staleTasks.length,
          staleTasks: u.staleTasks,
          // Hero Syndrome metrics
          criticalCount,
          criticalPercentage,
          isHero,
          criticalTasks: u.criticalTasks
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [sprintTasks, currentTeamMembers]);

  // Get sprint tasks for a specific user grouped by status
  const getSprintUserTasks = (username) => {
    const userTasks = sprintTasks.filter(t => t.username === username);
    
    // Calculate staleness for each task (days since last update)
    const enrichedTasks = userTasks.map(task => {
      let daysSinceUpdate = null;
      if (task.updated_date) {
        const updateDate = new Date(task.updated_date);
        const now = new Date();
        daysSinceUpdate = Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
      }
      return {
        ...task,
        daysSinceUpdate,
        isStale: daysSinceUpdate !== null && daysSinceUpdate > 3 && 
                 (task.status_status || "").toLowerCase().includes("in progress")
      };
    });
    
    return {
      username,
      vencidas: enrichedTasks.filter(t => t.urgency_status === "Vencida"),
      sinFecha: enrichedTasks.filter(t => t.urgency_status === "Sin fecha"),
      aTiempo: enrichedTasks.filter(t => t.urgency_status !== "Vencida" && t.urgency_status !== "Sin fecha")
    };
  };

  // Handle click on user in sprint chart
  const handleSprintUserClick = (username) => {
    const userData = getSprintUserTasks(username);
    setSprintUserDetail(userData);
    setShowSprintUserDetail(true);
  };

  // Calculate DHI (Delivery Health Index) for a user
  const calculateDHI = (username) => {
    const userTasks = sprintTasks.filter(t => t.username === username);
    const total = userTasks.length;
    
    if (total === 0) return { dhi: 100, reliability: 100, planning: 100, flow: 100, offenders: { overdue: [], noDate: [], zombies: [] } };
    
    // Reliability: % NOT overdue
    const overdueCount = userTasks.filter(t => t.urgency_status === "Vencida").length;
    const reliability = Math.round(((total - overdueCount) / total) * 100);
    
    // Planning: % with due_date
    const noDateCount = userTasks.filter(t => t.urgency_status === "Sin fecha").length;
    const planning = Math.round(((total - noDateCount) / total) * 100);
    
    // Flow: % with age < 30 days
    const zombieCount = userTasks.filter(t => (t.task_age_days || 0) >= 30).length;
    const flow = Math.round(((total - zombieCount) / total) * 100);
    
    // DHI = (Reliability * 0.4) + (Planning * 0.3) + (Flow * 0.3)
    const dhi = Math.round((reliability * 0.4) + (planning * 0.3) + (flow * 0.3));
    
    // Offenders (tasks that penalize the index)
    const offenders = {
      overdue: userTasks.filter(t => t.urgency_status === "Vencida"),
      noDate: userTasks.filter(t => t.urgency_status === "Sin fecha"),
      zombies: userTasks.filter(t => (t.task_age_days || 0) >= 30)
    };
    
    return { dhi, reliability, planning, flow, offenders };
  };

  // Handle DHI click to show diagnostic modal
  const handleDhiClick = (username) => {
    const dhiData = calculateDHI(username);
    setDhiDiagnostic({ username, ...dhiData });
    setShowDhiModal(true);
  };

  // Get DHI badge color
  const getDhiColor = (dhi) => {
    if (dhi >= 90) return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' };
    if (dhi >= 75) return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' };
    return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' };
  };

  // SQL Query for Raw Data tab
  const WORKLOAD_SQL_QUERY = `WITH tech_team AS (
    SELECT user_id, username, email, specialty, role_type FROM (
        VALUES 
        (4420171, 'Francisco Rios', 'rios.f@...', 'Front End Dev', 'Tecnología'),
        (4389316, 'Oscar Molinar', 'molinar.o@...', 'Tecnología', 'Tecnología'),
        -- ... (miembros del equipo seleccionado)
    ) AS t(user_id, username, email, specialty, role_type)
),
all_active_tasks AS (
    SELECT ta.id as assignee_id, t.id as task_id, 'Task' as item_type,
           t.name, t.status_status, t.priority, t.due_date, t.date_created, ...
    FROM datahub_operations.clickup.task t
    INNER JOIN datahub_operations.clickup.task_assignee ta ON t.id = ta.task_id
    WHERE t._fivetran_deleted = false AND t.status_type != 'closed'
    
    UNION ALL
    
    SELECT sta.id, st.id, 'Subtask', st.name, st.status_status, ...
    FROM datahub_operations.clickup.sub_task st
    INNER JOIN datahub_operations.clickup.sub_task_assignee sta ON st.id = sta.sub_task_id
    WHERE st._fivetran_deleted = false AND st.status_type != 'closed'
)
SELECT tt.username, tt.specialty, tt.role_type, aat.task_name, aat.status_status,
       aat.priority, s.name as space_name, creator.username as created_by,
       FROM_UNIXTIME(aat.due_date / 1000) as due_date,
       CASE WHEN due_date IS NULL THEN 'Sin fecha'
            WHEN due_date < NOW() THEN 'Vencida'
            WHEN due_date <= NOW() + INTERVAL 7 DAY THEN 'Próximos 7 días'
            WHEN due_date <= NOW() + INTERVAL 30 DAY THEN 'Próximos 8-30 días'
            ELSE 'Más de 30 días'
       END as urgency_status,
       ROUND((date_updated - date_created) / 86400000, 1) as task_age_days,
       aat.url
FROM tech_team tt
INNER JOIN all_active_tasks aat ON tt.user_id = aat.assignee_id
LEFT JOIN datahub_operations.clickup.space s ON aat.space_id = s.id
LEFT JOIN datahub_operations.clickup.team_member creator ON aat.creator_id = creator.user_id
ORDER BY tt.username, urgency_status`;

  // Filtered raw data for Raw Data tab
  const filteredRawData = useMemo(() => {
    if (!data) return [];
    return data.tasks.filter(task => {
      // Search filter (name or task_name)
      if (rawDataSearch) {
        const searchLower = rawDataSearch.toLowerCase();
        const matchesName = task.username?.toLowerCase().includes(searchLower);
        const matchesTask = task.task_name?.toLowerCase().includes(searchLower);
        const matchesSpace = task.space_name?.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesTask && !matchesSpace) return false;
      }
      // Status filter
      if (rawDataStatus !== "all" && task.status_status !== rawDataStatus) return false;
      // Urgency filter
      if (rawDataUrgency !== "all" && task.urgency_status !== rawDataUrgency) return false;
      return true;
    });
  }, [data, rawDataSearch, rawDataStatus, rawDataUrgency]);

  // Get unique statuses for filter
  const uniqueStatuses = useMemo(() => {
    if (!data) return [];
    const statuses = [...new Set(data.tasks.map(t => t.status_status))];
    return statuses.sort();
  }, [data]);

  // Copy query to clipboard
  const handleCopyQuery = async () => {
    await navigator.clipboard.writeText(WORKLOAD_SQL_QUERY);
    setCopiedQuery(true);
    setTimeout(() => setCopiedQuery(false), 2000);
    toast.success("Query copiado al portapapeles");
  };

  // Export filtered data to CSV
  const handleExportCSV = () => {
    if (filteredRawData.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    // Track export
    trackActivity("export", { export_format: "csv", item_count: filteredRawData.length });

    // Define CSV columns
    const headers = [
      'Usuario', 'Email', 'Especialidad', 'Rol', 'Tarea', 'Estado', 'Prioridad',
      'Space', 'Creado Por', 'Fecha Due', 'Urgencia', 'Días Activo', 'URL'
    ];

    // Convert data to CSV rows
    const rows = filteredRawData.map(task => [
      task.username || '',
      task.email || '',
      task.specialty || '',
      task.role_type || '',
      `"${(task.task_name || '').replace(/"/g, '""')}"`, // Escape quotes in task name
      task.status_status || '',
      task.priority || '',
      task.space_name || '',
      task.created_by || '',
      task.due_date ? new Date(task.due_date).toLocaleDateString('es-ES') : 'Sin fecha',
      task.urgency_status || '',
      Math.round(task.task_age_days || 0),
      task.url || ''
    ]);

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `workload_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exportado ${filteredRawData.length} registros a CSV`);
  };

  // Recalculate user workloads based on filters
  const filteredUserWorkloads = useMemo(() => {
    if (!data) return [];
    
    const userMap = {};
    filteredTasks.forEach(task => {
      if (!userMap[task.username]) {
        userMap[task.username] = {
          username: task.username,
          email: task.email,
          specialty: task.specialty,
          role_type: task.role_type,
          total_tasks: 0,
          overdue_tasks: 0,
          no_date_tasks: 0,
          next_7_days: 0,
          next_8_30_days: 0,
          more_than_30_days: 0,
          high_priority_overdue: 0,
          age_sum: 0
        };
      }
      
      const u = userMap[task.username];
      u.total_tasks++;
      u.age_sum += task.task_age_days || 0;
      
      if (task.urgency_status === "Vencida") {
        u.overdue_tasks++;
        if (task.priority && ["urgent", "high"].includes(task.priority.toLowerCase())) {
          u.high_priority_overdue++;
        }
      } else if (task.urgency_status === "Sin fecha") {
        u.no_date_tasks++;
      } else if (task.urgency_status === "Próximos 7 días") {
        u.next_7_days++;
      } else if (task.urgency_status === "Próximos 8-30 días") {
        u.next_8_30_days++;
      } else {
        u.more_than_30_days++;
      }
    });
    
    return Object.values(userMap)
      .map(u => ({ ...u, avg_task_age: u.total_tasks > 0 ? Math.round(u.age_sum / u.total_tasks * 10) / 10 : 0 }))
      .sort((a, b) => b.total_tasks - a.total_tasks);
  }, [filteredTasks]);

  // Status distribution for pie chart
  const statusChartData = useMemo(() => {
    if (!data) return [];
    const counts = {};
    filteredTasks.forEach(t => {
      counts[t.status_status] = (counts[t.status_status] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredTasks]);

  // Scatter plot data for aging analysis
  const agingScatterData = useMemo(() => {
    if (!data) return [];
    const specialtyAging = {};
    
    filteredTasks.forEach(task => {
      const key = task.specialty || "Sin especialidad";
      if (!specialtyAging[key]) {
        specialtyAging[key] = { tasks: [], ageSum: 0 };
      }
      specialtyAging[key].tasks.push(task);
      specialtyAging[key].ageSum += task.task_age_days || 0;
    });
    
    return Object.entries(specialtyAging).map(([specialty, data]) => ({
      specialty,
      avgAge: Math.round(data.ageSum / data.tasks.length),
      count: data.tasks.length,
      maxAge: Math.max(...data.tasks.map(t => t.task_age_days || 0))
    }));
  }, [filteredTasks]);

  // User bar chart data
  const userChartData = useMemo(() => {
    return filteredUserWorkloads.map(u => ({
      name: u.username.split(" ")[0],
      fullName: u.username,
      Vencida: u.overdue_tasks,
      "Sin fecha": u.no_date_tasks,
      "Próximos 7 días": u.next_7_days,
      "Próximos 8-30 días": u.next_8_30_days,
      "Más de 30 días": u.more_than_30_days,
      total: u.total_tasks
    }));
  }, [filteredUserWorkloads]);

  // Get user's overdue high-priority tasks
  const getUserOverdueTasks = (username) => {
    return filteredTasks.filter(t => 
      t.username === username && 
      t.urgency_status === "Vencida"
    ).sort((a, b) => {
      const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
      return (priorityOrder[a.priority?.toLowerCase()] || 2) - (priorityOrder[b.priority?.toLowerCase()] || 2);
    });
  };

  const handleUserClick = (username) => {
    setSelectedUser(username);
    setShowUserTasks(true);
  };

  // Calculate KPIs from filtered data
  const kpis = useMemo(() => {
    const total = filteredTasks.length;
    const overdue = filteredTasks.filter(t => t.urgency_status === "Vencida").length;
    const noDate = filteredTasks.filter(t => t.urgency_status === "Sin fecha").length;
    const noPriority = filteredTasks.filter(t => !t.priority || t.priority.toLowerCase() === "normal").length;
    
    return {
      total,
      overdue,
      noDate,
      overduePercent: total > 0 ? Math.round(overdue / total * 100) : 0,
      noDatePercent: total > 0 ? Math.round(noDate / total * 100) : 0,
      noPriorityPercent: total > 0 ? Math.round(noPriority / total * 100) : 0
    };
  }, [filteredTasks]);

  // =============================================
  // CLIENT STRATEGY TAB DATA
  // =============================================

  // 1. Client Concentration Matrix (Heatmap data)
  const clientConcentrationMatrix = useMemo(() => {
    if (!data) return { users: [], spaces: [], matrix: [] };
    
    // Helper to generate client initials (2-3 chars)
    const getClientInitials = (spaceName) => {
      if (!spaceName) return "??";
      // Remove common prefixes
      const cleaned = spaceName
        .replace(/^(CBU|GBU|XBU|LDM)\s*-?\s*/i, "")
        .replace(/^Perly\s*-?\s*/i, "P:")
        .trim();
      
      const words = cleaned.split(/[\s\-_]+/).filter(w => w.length > 0);
      if (words.length === 0) return spaceName.substring(0, 2).toUpperCase();
      if (words.length === 1) return words[0].substring(0, 3).toUpperCase();
      // Take first letter of first 2-3 words
      return words.slice(0, 3).map(w => w[0]).join("").toUpperCase();
    };
    
    // Get active tasks (to do + in progress) from sprint tasks
    const activeTasks = sprintTasks.filter(t => {
      const status = (t.status_status || "").toLowerCase();
      return status.includes("to do") || status.includes("in progress") || status === "open";
    });
    
    // Count tasks per space
    const spaceTaskCounts = {};
    activeTasks.forEach(t => {
      const space = t.space_name || "Sin espacio";
      spaceTaskCounts[space] = (spaceTaskCounts[space] || 0) + 1;
    });
    
    // Get ALL spaces sorted by activity (no "Otros" grouping)
    const sortedSpaces = Object.entries(spaceTaskCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
    
    // Build user-space matrix using current team members
    const userSpaceMatrix = {};
    currentTeamMembers.forEach(member => {
      userSpaceMatrix[member.username] = {};
      sortedSpaces.forEach(space => {
        userSpaceMatrix[member.username][space] = 0;
      });
    });
    
    // Count tasks per user-space
    activeTasks.forEach(t => {
      if (userSpaceMatrix[t.username]) {
        const space = t.space_name || "Sin espacio";
        if (userSpaceMatrix[t.username][space] !== undefined) {
          userSpaceMatrix[t.username][space]++;
        }
      }
    });
    
    // Convert to array format for rendering
    const matrixData = Object.entries(userSpaceMatrix)
      .filter(([_, spaces]) => Object.values(spaces).some(v => v > 0))
      .map(([username, spaces]) => ({
        username,
        shortName: username.split(" ")[0],
        ...spaces,
        total: Object.values(spaces).reduce((sum, v) => sum + v, 0)
      }))
      .sort((a, b) => b.total - a.total);
    
    // Build space info with initials
    const spacesWithInitials = sortedSpaces
      .filter(s => matrixData.some(row => row[s] > 0))
      .map(space => ({
        name: space,
        initials: getClientInitials(space)
      }));
    
    return {
      users: matrixData,
      spaces: spacesWithInitials
    };
  }, [sprintTasks, data, currentTeamMembers]);

  // 1.5 Resource Swarm Matrix (Scatter plot data)
  const resourceSwarmData = useMemo(() => {
    if (!data) return { clients: [], medianX: 10, medianY: 3 };
    
    // Get active tasks (to do + in progress) from sprint tasks
    const activeTasks = sprintTasks.filter(t => {
      const status = (t.status_status || "").toLowerCase();
      return status.includes("to do") || status.includes("in progress") || status === "open";
    });
    
    // Group by space
    const spaceData = {};
    activeTasks.forEach(t => {
      const space = t.space_name || "Sin espacio";
      if (!spaceData[space]) {
        spaceData[space] = {
          tasks: 0,
          urgentTasks: 0,
          users: new Set(),
          userNames: []
        };
      }
      spaceData[space].tasks++;
      if (t.username) {
        spaceData[space].users.add(t.username);
      }
      
      // Count urgent/high priority
      const priority = (t.priority || "").toLowerCase();
      if (priority === "urgent" || priority === "urgente" || priority === "high" || priority === "alta") {
        spaceData[space].urgentTasks++;
      }
    });
    
    // Convert to array for scatter plot
    const clients = Object.entries(spaceData)
      .map(([space, data]) => {
        const userCount = data.users.size;
        const taskCount = data.tasks;
        const userList = Array.from(data.users);
        
        return {
          name: space,
          shortName: space.length > 20 ? space.substring(0, 20) + "..." : space,
          x: taskCount,  // Volume (tasks)
          y: userCount,  // Involvement (unique users)
          z: Math.max(data.urgentTasks * 100, 50), // Bubble size (min 50 for visibility)
          urgentTasks: data.urgentTasks,
          userList: userList.slice(0, 5),
          hasMoreUsers: userList.length > 5
        };
      })
      .filter(c => c.x > 0); // Only show clients with active tasks
    
    // Calculate medians for quadrant lines
    const xValues = clients.map(c => c.x).sort((a, b) => a - b);
    const yValues = clients.map(c => c.y).sort((a, b) => a - b);
    
    const medianX = xValues.length > 0 
      ? xValues[Math.floor(xValues.length / 2)] 
      : 10;
    const medianY = yValues.length > 0 
      ? yValues[Math.floor(yValues.length / 2)] 
      : 3;
    
    // Use at least sensible minimums
    const finalMedianX = Math.max(medianX, 5);
    const finalMedianY = Math.max(medianY, 2);
    
    // Assign quadrant to each client
    const clientsWithQuadrant = clients.map(c => {
      let quadrant, quadrantLabel, quadrantColor, action;
      
      if (c.x >= finalMedianX && c.y >= finalMedianY) {
        quadrant = "strategic";
        quadrantLabel = "Strategic Accounts";
        quadrantColor = "#22c55e"; // green
        action = "Monitorear calidad";
      } else if (c.x < finalMedianX && c.y >= finalMedianY) {
        quadrant = "crowded";
        quadrantLabel = "Crowded Kitchen";
        quadrantColor = "#f59e0b"; // amber
        action = "Reducir equipo";
      } else if (c.x >= finalMedianX && c.y < finalMedianY) {
        quadrant = "burnout";
        quadrantLabel = "Burnout Zone";
        quadrantColor = "#ef4444"; // red
        action = "Agregar refuerzos";
      } else {
        quadrant = "maintenance";
        quadrantLabel = "Maintenance Mode";
        quadrantColor = "#3b82f6"; // blue
        action = "Mantener así";
      }
      
      return { ...c, quadrant, quadrantLabel, quadrantColor, action };
    });
    
    return {
      clients: clientsWithQuadrant,
      medianX: finalMedianX,
      medianY: finalMedianY
    };
  }, [sprintTasks, data]);

  // 2. Firefighting Index (Reactivity)
  const firefightingIndex = useMemo(() => {
    if (!data) return { reactive: 0, planned: 0, reactivePercent: 0, isHighReactivity: false };
    
    // Filter tasks that are "in progress"
    const inProgressTasks = sprintTasks.filter(t => {
      const status = (t.status_status || "").toLowerCase();
      return status.includes("in progress");
    });
    
    if (inProgressTasks.length === 0) {
      return { reactive: 0, planned: 0, reactivePercent: 0, isHighReactivity: false, total: 0 };
    }
    
    const now = new Date();
    const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    
    let reactive = 0;
    let planned = 0;
    
    inProgressTasks.forEach(t => {
      if (t.created_date) {
        const createdDate = new Date(t.created_date);
        if (createdDate >= fortyEightHoursAgo) {
          reactive++;
        } else {
          planned++;
        }
      } else {
        planned++; // If no date, assume planned
      }
    });
    
    const total = reactive + planned;
    const reactivePercent = total > 0 ? Math.round((reactive / total) * 100) : 0;
    
    return {
      reactive,
      planned,
      total,
      reactivePercent,
      plannedPercent: 100 - reactivePercent,
      isHighReactivity: reactivePercent > 30
    };
  }, [sprintTasks, data]);

  // 3. Client Neglect Radar (Average task age per client)
  const clientNeglectData = useMemo(() => {
    if (!data) return [];
    
    // Group active tasks by space and calculate average age
    const spaceAges = {};
    
    sprintTasks.forEach(t => {
      const space = t.space_name || "Sin espacio";
      if (!spaceAges[space]) {
        spaceAges[space] = { totalAge: 0, count: 0 };
      }
      spaceAges[space].totalAge += t.task_age_days || 0;
      spaceAges[space].count++;
    });
    
    // Convert to array and calculate averages
    return Object.entries(spaceAges)
      .map(([space, data]) => ({
        space,
        shortSpace: space.length > 15 ? space.substring(0, 15) + "..." : space,
        avgAge: Math.round(data.totalAge / data.count),
        taskCount: data.count,
        isNeglected: Math.round(data.totalAge / data.count) > 45
      }))
      .sort((a, b) => b.avgAge - a.avgAge)
      .slice(0, 15); // Top 15 clients
  }, [sprintTasks, data]);

  // Helper function to get heatmap cell color
  const getHeatmapColor = (value) => {
    if (value === 0) return "bg-slate-100";
    if (value <= 2) return "bg-blue-200";
    if (value <= 4) return "bg-blue-400";
    return "bg-red-500";
  };

  const getHeatmapTextColor = (value) => {
    if (value === 0) return "text-slate-400";
    if (value <= 4) return "text-slate-800";
    return "text-white";
  };

  // 4. The Panic Index (Client Priority Abuse)
  const panicIndexData = useMemo(() => {
    if (!data) return [];
    
    // Group tasks by space and count priorities
    const spacePriorities = {};
    
    sprintTasks.forEach(t => {
      const space = t.space_name || "Sin espacio";
      if (!spacePriorities[space]) {
        spacePriorities[space] = { urgent: 0, high: 0, total: 0 };
      }
      spacePriorities[space].total++;
      
      const priority = (t.priority || "").toLowerCase();
      if (priority === "urgent" || priority === "urgente") {
        spacePriorities[space].urgent++;
      } else if (priority === "high" || priority === "alta") {
        spacePriorities[space].high++;
      }
    });
    
    // Calculate panic percentage
    return Object.entries(spacePriorities)
      .map(([space, data]) => {
        const panicCount = data.urgent + data.high;
        const panicPercent = data.total > 0 ? Math.round((panicCount / data.total) * 100) : 0;
        return {
          space,
          shortSpace: space.length > 20 ? space.substring(0, 20) + "..." : space,
          urgent: data.urgent,
          high: data.high,
          total: data.total,
          panicCount,
          panicPercent,
          status: panicPercent > 50 ? "toxic" : panicPercent < 10 ? "healthy" : "normal"
        };
      })
      .filter(d => d.total >= 2) // At least 2 tasks
      .sort((a, b) => b.panicPercent - a.panicPercent)
      .slice(0, 12);
  }, [sprintTasks, data]);

  // 5. SLA Equity Radar (Diverging from global average)
  const slaEquityData = useMemo(() => {
    if (!data || sprintTasks.length === 0) return { clients: [], globalAvg: 0 };
    
    // Calculate global average task age
    const totalAge = sprintTasks.reduce((sum, t) => sum + (t.task_age_days || 0), 0);
    const globalAvg = Math.round(totalAge / sprintTasks.length);
    
    // Group by space and calculate average age
    const spaceAges = {};
    sprintTasks.forEach(t => {
      const space = t.space_name || "Sin espacio";
      if (!spaceAges[space]) {
        spaceAges[space] = { totalAge: 0, count: 0 };
      }
      spaceAges[space].totalAge += t.task_age_days || 0;
      spaceAges[space].count++;
    });
    
    // Calculate deviation from global average
    const clients = Object.entries(spaceAges)
      .map(([space, data]) => {
        const avgAge = Math.round(data.totalAge / data.count);
        const deviation = avgAge - globalAvg;
        return {
          space,
          shortSpace: space.length > 18 ? space.substring(0, 18) + "..." : space,
          avgAge,
          deviation,
          taskCount: data.count,
          status: deviation > 15 ? "neglected" : deviation < -15 ? "goldplating" : "balanced"
        };
      })
      .filter(d => d.taskCount >= 2)
      .sort((a, b) => b.deviation - a.deviation);
    
    return { clients, globalAvg };
  }, [sprintTasks, data]);

  // 6. Top Demand Generators (Requester Quality)
  const demandGeneratorsData = useMemo(() => {
    if (!data) return [];
    
    // Group by creator
    const creatorStats = {};
    
    sprintTasks.forEach(t => {
      const creator = t.created_by || t.creator_email || "Desconocido";
      if (!creatorStats[creator]) {
        creatorStats[creator] = { 
          total: 0, 
          zombies: 0,
          spaces: new Set()
        };
      }
      creatorStats[creator].total++;
      creatorStats[creator].spaces.add(t.space_name || "Sin espacio");
      
      // Check if zombie (in progress > 30 days)
      const status = (t.status_status || "").toLowerCase();
      const isInProgress = status.includes("in progress");
      const age = t.task_age_days || 0;
      
      if (isInProgress && age > 30) {
        creatorStats[creator].zombies++;
      }
    });
    
    // Calculate zombie rate and sort
    return Object.entries(creatorStats)
      .map(([creator, stats]) => ({
        creator,
        shortCreator: creator.length > 25 ? creator.substring(0, 25) + "..." : creator,
        ticketsCreated: stats.total,
        zombies: stats.zombies,
        zombieRate: stats.total > 0 ? Math.round((stats.zombies / stats.total) * 100) : 0,
        clientsCount: stats.spaces.size,
        isProblematic: stats.total >= 5 && (stats.zombies / stats.total) > 0.2
      }))
      .filter(d => d.ticketsCreated >= 3)
      .sort((a, b) => b.ticketsCreated - a.ticketsCreated)
      .slice(0, 15);
  }, [sprintTasks, data]);

  // Show Team Configuration View
  if (showTeamConfig) {
    return (
      <TeamConfigView 
        onBack={() => {
          setShowTeamConfig(false);
          loadTeams(); // Reload teams when coming back
        }}
        currentUser={null} // TODO: Pass current user if needed for permissions
      />
    );
  }

  // Show "Create Team First" screen if no teams exist or no team is selected
  if (!teamsLoading && (teams.length === 0 || selectedTeamId === "all")) {
    return (
      <div className="min-h-screen bg-[#e8eef3] pb-20">
        {/* Header */}
        <div className="border-b bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={onBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-orange-500" />
                  Análisis de Carga de Trabajo
                </h1>
                <p className="text-sm text-slate-500">Configura tu equipo para comenzar</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Create Team Prompt */}
        <div className="max-w-2xl mx-auto px-6 py-12">
          <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-orange-600" />
              </div>
              
              {teams.length === 0 ? (
                <>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">
                    ¡Bienvenido al Análisis de Carga!
                  </h2>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    Para comenzar, necesitas crear tu primer equipo. Un equipo es un grupo de 
                    personas de ClickUp cuyos datos quieres analizar juntos.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">
                    Selecciona un Equipo
                  </h2>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    Tienes {teams.length} equipo{teams.length > 1 ? 's' : ''} creado{teams.length > 1 ? 's' : ''}. 
                    Selecciona uno para ver su análisis de carga de trabajo.
                  </p>
                  
                  {/* Quick Team Select */}
                  <div className="mb-6 max-w-sm mx-auto">
                    <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Selecciona un equipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name} ({team.members?.length || 0} miembros)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="text-slate-500 text-sm mb-4">— o —</div>
                </>
              )}
              
              <Button 
                onClick={() => setShowTeamConfig(true)}
                className="gap-2 bg-orange-600 hover:bg-orange-700"
                size="lg"
              >
                <Plus className="h-5 w-5" />
                {teams.length === 0 ? 'Crear mi primer equipo' : 'Crear nuevo equipo'}
              </Button>
              
              <p className="text-xs text-slate-500 mt-6">
                Podrás agregar o modificar miembros en cualquier momento
              </p>
            </CardContent>
          </Card>
        </div>
        
        <WorkloadHelpButton />
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <div className="min-h-screen bg-[#e8eef3] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-600">Cargando datos de ClickUp...</p>
        </div>
      </div>
    );
  }

  // Get current team name for subtitle
  const currentTeamName = selectedTeamId !== "all" 
    ? teams.find(t => t.id === selectedTeamId)?.name 
    : "Todos los usuarios";

  return (
    <div className="min-h-screen bg-[#e8eef3] pb-20">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={onBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-orange-500" />
                  Análisis de Carga de Trabajo
                </h1>
                <p className="text-sm text-slate-500">{currentTeamName} - Datos de ClickUp</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Data Source indicator */}
              {cacheInfo && (
                <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
                  cacheInfo.data_source === 'lakebase' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  <Database className="h-3 w-3" />
                  <span className="font-medium">
                    {cacheInfo.data_source === 'lakebase' ? 'Lakebase' : 'Lakehouse'}
                  </span>
                </div>
              )}
              {/* Cache indicator with both timestamps */}
              {cacheInfo && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-3 text-xs bg-slate-100 px-3 py-1.5 rounded-full cursor-help">
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <RefreshCw className="h-3 w-3" />
                          <span>Cache: {formatCacheDate(cacheInfo.cached_at)}</span>
                        </div>
                        {formatSyncDate(cacheInfo.last_clickup_sync) && (
                          <>
                            <div className="w-px h-3 bg-slate-300" />
                            <div className="flex items-center gap-1.5 text-orange-600">
                              <Database className="h-3 w-3" />
                              <span>ClickUp: {formatSyncDate(cacheInfo.last_clickup_sync)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div className="space-y-1 text-xs">
                        <p><strong>🔄 Cache Datahub:</strong> Última vez que se consultó la base de datos</p>
                        <p><strong>📡 Sync ClickUp:</strong> Última sincronización de datos desde ClickUp (Fivetran)</p>
                        <p><strong>💾 Fuente:</strong> {cacheInfo.data_source === 'lakebase' ? 'Lakebase (PostgreSQL)' : 'Databricks Lakehouse'}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button 
                variant="outline" 
                onClick={() => fetchWorkloadData(true)}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Forzar Actualización
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Global Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <Label className="text-sm text-slate-600">Equipo:</Label>
              </div>
              <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Selecciona un equipo" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {team.name} ({team.members?.length || 0})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex-1" />
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTeamConfig(true)}
                      className="gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Configurar Equipos
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Crear o editar equipos para filtrar el análisis
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-3xl grid-cols-4">
            <TabsTrigger value="current-sprint" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Current Sprint
            </TabsTrigger>
            <TabsTrigger value="client-strategy" className="flex items-center gap-2">
              <Grid3X3 className="h-4 w-4" />
              Client Strategy
            </TabsTrigger>
            <TabsTrigger value="pipeline-governance" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Pipeline Governance
            </TabsTrigger>
            <TabsTrigger value="raw-data" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Raw Data
            </TabsTrigger>
          </TabsList>

          {/* ================================================== */}
          {/* CURRENT SPRINT TAB - Tactical Daily Standup View */}
          {/* ================================================== */}
          <TabsContent value="current-sprint" className="space-y-6">
            {/* Excluded statuses banner */}
            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-emerald-100">
                    <Zap className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-emerald-800">Vista Táctica - Daily Standup</p>
                    <p className="text-sm text-emerald-700 mt-1">
                      Enfocada 100% en ejecución y desbloqueo inmediato. 
                      <span className="font-medium"> Se excluyen automáticamente:</span> ready for review, waiting for feedback, waiting for review, send ok to enovia.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sprint Health KPIs */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Target className="h-5 w-5 text-emerald-500" />
                Sprint Health
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* WIP */}
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">WIP Total</p>
                        <p className="text-3xl font-bold text-slate-900">{sprintKPIs.wip}</p>
                        <p className="text-xs text-slate-500 mt-1">Tareas en ejecución</p>
                      </div>
                      <div className="p-3 rounded-full bg-blue-100">
                        <Play className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Vencidas */}
                <Card className={`border-l-4 ${sprintKPIs.overdueCount > 0 ? 'border-l-red-500' : 'border-l-emerald-500'}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Vencidas</p>
                        <p className={`text-3xl font-bold ${sprintKPIs.overdueCount > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {sprintKPIs.overdueCount}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">Requieren atención urgente</p>
                      </div>
                      <div className={`p-3 rounded-full ${sprintKPIs.overdueCount > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                        <AlertTriangle className={`h-6 w-6 ${sprintKPIs.overdueCount > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Planning Hygiene */}
                <Card className={`border-l-4 ${sprintKPIs.planningHygiene > 20 ? 'border-l-amber-500' : 'border-l-emerald-500'}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Sin Fecha</p>
                        <p className={`text-3xl font-bold ${sprintKPIs.planningHygiene > 20 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {sprintKPIs.noDateCount}
                        </p>
                        <p className={`text-xs mt-1 ${sprintKPIs.planningHygiene > 20 ? 'text-amber-600' : 'text-slate-500'}`}>
                          {sprintKPIs.planningHygiene}% del total
                        </p>
                      </div>
                      <div className={`p-3 rounded-full ${sprintKPIs.planningHygiene > 20 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                        <Calendar className={`h-6 w-6 ${sprintKPIs.planningHygiene > 20 ? 'text-amber-600' : 'text-emerald-600'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Zombie Count */}
                <Card className={`border-l-4 ${sprintKPIs.zombieCount > 0 ? 'border-l-purple-500' : 'border-l-slate-300'}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">Zombies</p>
                        <p className={`text-3xl font-bold ${sprintKPIs.zombieCount > 0 ? 'text-purple-600' : 'text-slate-400'}`}>
                          {sprintKPIs.zombieCount}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">In Progress +30 días</p>
                      </div>
                      <div className={`p-3 rounded-full ${sprintKPIs.zombieCount > 0 ? 'bg-purple-100' : 'bg-slate-100'}`}>
                        <Skull className={`h-6 w-6 ${sprintKPIs.zombieCount > 0 ? 'text-purple-600' : 'text-slate-400'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Execution Load Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-emerald-500" />
                  Carga de Ejecución por Usuario
                </CardTitle>
                <CardDescription>
                  Solo tareas operativas activas. Haz clic en un usuario para ver el detalle.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: Math.max(400, sprintUserWorkloads.length * 45) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sprintUserWorkloads} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={100}
                        tick={{ fontSize: 11 }}
                        interval={0}
                      />
                      <RechartsTooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
                                <p className="font-semibold mb-2">{data.fullName}</p>
                                <p className="text-red-600">🔴 Vencidas: {data.Vencida}</p>
                                <p className="text-slate-500">⚪ Sin fecha: {data["Sin fecha"]}</p>
                                <p className="text-emerald-600">🟢 A tiempo: {data["A tiempo"]}</p>
                                <p className="font-semibold mt-2 border-t pt-2">Total: {data.total}</p>
                                <p className="text-xs text-blue-500 mt-1">Click para ver detalle</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="Vencida" 
                        stackId="a" 
                        fill="#ef4444"
                        onClick={(data) => handleSprintUserClick(data.fullName)}
                        cursor="pointer"
                      />
                      <Bar 
                        dataKey="Sin fecha" 
                        stackId="a" 
                        fill="#94a3b8"
                        onClick={(data) => handleSprintUserClick(data.fullName)}
                        cursor="pointer"
                      />
                      <Bar 
                        dataKey="A tiempo" 
                        stackId="a" 
                        fill="#10b981" 
                        radius={[0, 4, 4, 0]}
                        onClick={(data) => handleSprintUserClick(data.fullName)}
                        cursor="pointer"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* User Task Summary Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Resumen por Usuario</CardTitle>
                    <CardDescription>Todos los miembros del equipo ({sprintUserWorkloads.length}). Haz clic en cualquier número para ver el detalle.</CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowIndicatorsHelp(true)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <HelpCircle className="h-5 w-5 mr-1" />
                    <span className="text-sm">Guía</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead className="w-[100px]">Foco</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">
                        <span className="flex items-center justify-center gap-1">
                          🔴 Vencidas
                        </span>
                      </TableHead>
                      <TableHead className="text-center">
                        <span className="flex items-center justify-center gap-1">
                          ⚪ Sin Fecha
                        </span>
                      </TableHead>
                      <TableHead className="text-center">
                        <span className="flex items-center justify-center gap-1">
                          🟢 A Tiempo
                        </span>
                      </TableHead>
                      <TableHead className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center justify-center gap-1 cursor-help">
                                📊 DHI
                                <Info className="h-3 w-3 text-slate-400" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="font-semibold mb-1">Delivery Health Index</p>
                              <p className="text-xs">
                                Índice compuesto: Reliability (40%) + Planning (30%) + Flow (30%)
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sprintUserWorkloads.map((user) => (
                      <TableRow 
                        key={user.fullName} 
                        className={`hover:bg-slate-50 ${user.total === 0 ? 'bg-amber-50/50' : ''} ${user.isHero ? 'bg-amber-50/30' : ''}`}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {/* Hero Syndrome Indicator */}
                            {user.isHero && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <div className="relative">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center ring-2 ring-yellow-400 ring-offset-1 animate-pulse">
                                        <span className="text-sm font-bold text-white">{user.name.charAt(0)}</span>
                                      </div>
                                      <Crown className="h-4 w-4 text-yellow-500 absolute -top-2 -right-1 drop-shadow-md" fill="#eab308" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs !bg-slate-900 !text-white border border-amber-500/50">
                                    <p className="font-semibold text-amber-400 mb-1">🦸 RIESGO DE HÉROE</p>
                                    <p className="text-sm text-white mb-2">
                                      <span className="font-medium">{user.fullName}</span> concentra el <span className="text-amber-400 font-bold">{user.criticalPercentage}%</span> de la prioridad crítica del Sprint.
                                    </p>
                                    <p className="text-xs text-red-400 mb-2">Si falla, el sprint peligra.</p>
                                    <div className="text-xs text-slate-300">
                                      <p>📊 {user.criticalCount} tareas Urgent/High de {sprintUserWorkloads.reduce((sum, u) => sum + u.criticalCount, 0)} totales</p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {user.total === 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <UserX className="h-4 w-4 text-amber-500" />
                                  </TooltipTrigger>
                                  <TooltipContent className="!bg-slate-900 !text-white border border-slate-700">
                                    <p className="text-xs text-slate-200">Sin tareas activas en ClickUp</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {/* Priority Inversion Alert */}
                            {user.hasPriorityInversion && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertCircle className="h-4 w-4 text-orange-500 animate-pulse" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs !bg-slate-900 !text-white border border-slate-700">
                                    <p className="font-semibold text-orange-400 mb-1">⚠️ Prioridad Invertida</p>
                                    <p className="text-xs mb-2 text-slate-300">Trabajando en baja prioridad con alta prioridad en espera.</p>
                                    <div className="text-xs space-y-1">
                                      <p className="text-red-400">🔴 Alta prioridad pendiente: {user.pendingHighPriority.length}</p>
                                      <p className="text-blue-400">🔵 En progreso (baja): {user.inProgressLowPriority.length}</p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {/* Stale Tasks Alert */}
                            {user.staleCount > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Clock className="h-4 w-4 text-purple-500" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs !bg-slate-900 !text-white border border-slate-700">
                                    <p className="font-semibold text-purple-300 mb-1">🕐 {user.staleCount} Tarea{user.staleCount > 1 ? 's' : ''} Estancada{user.staleCount > 1 ? 's' : ''}</p>
                                    <p className="text-xs mb-2 text-slate-300">Tareas &quot;In Progress&quot; sin actualizar en más de 3 días.</p>
                                    <div className="text-xs space-y-1 max-h-32 overflow-y-auto">
                                      {user.staleTasks.slice(0, 5).map((task, i) => (
                                        <p key={i} className="text-white truncate">
                                          • {task.task_name} <span className="text-amber-400">({task.daysSinceUpdate}d)</span>
                                        </p>
                                      ))}
                                      {user.staleTasks.length > 5 && (
                                        <p className="text-slate-400">...y {user.staleTasks.length - 5} más</p>
                                      )}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <span>{user.fullName}</span>
                          </div>
                        </TableCell>
                        {/* Focus Dispersion Column */}
                        <TableCell>
                          {user.total === 0 ? (
                            <span className="text-xs text-slate-400">-</span>
                          ) : user.spaceCount <= 2 ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-xs">
                                    Enfocado
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="!bg-slate-900 !text-white border border-slate-700">
                                  <p className="text-xs text-slate-200">{user.spaceCount} proyecto{user.spaceCount !== 1 ? 's' : ''}: <span className="text-blue-300">{user.spaces.join(', ')}</span></p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge className="bg-orange-50 text-orange-600 border-orange-200 text-xs flex items-center gap-1">
                                    <Shuffle className="h-3 w-3" />
                                    {user.spaceCount}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs !bg-slate-900 !text-white border border-slate-700">
                                  <p className="font-semibold text-orange-400 mb-1">⚠️ Dispersión de Foco</p>
                                  <p className="text-xs mb-2 text-slate-300">{user.spaceCount} proyectos diferentes. Alto cambio de contexto.</p>
                                  <div className="text-xs text-slate-200">
                                    {user.spaces.slice(0, 5).map((s, i) => <p key={i} className="text-white">• {s}</p>)}
                                    {user.spaces.length > 5 && <p className="text-slate-400">...y {user.spaces.length - 5} más</p>}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {user.total === 0 ? (
                            <span className="text-amber-600">0</span>
                          ) : user.total}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`${user.Vencida > 0 ? 'text-red-600 hover:bg-red-50' : 'text-slate-400'}`}
                            onClick={() => handleSprintUserClick(user.fullName)}
                            disabled={user.Vencida === 0}
                          >
                            {user.Vencida}
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`${user["Sin fecha"] > 0 ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-400'}`}
                            onClick={() => handleSprintUserClick(user.fullName)}
                            disabled={user["Sin fecha"] === 0}
                          >
                            {user["Sin fecha"]}
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`${user["A tiempo"] > 0 ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400'}`}
                            onClick={() => handleSprintUserClick(user.fullName)}
                            disabled={user["A tiempo"] === 0}
                          >
                            {user["A tiempo"]}
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          {user.total === 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
                              N/A
                            </Badge>
                          ) : (() => {
                            const dhiData = calculateDHI(user.fullName);
                            const colors = getDhiColor(dhiData.dhi);
                            return (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge 
                                      className={`${colors.bg} ${colors.text} border ${colors.border} cursor-pointer hover:opacity-80 transition-opacity`}
                                      onClick={() => handleDhiClick(user.fullName)}
                                    >
                                      {dhiData.dhi}%
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="font-semibold mb-2">DHI: {dhiData.dhi}%</p>
                                    <div className="space-y-1 text-xs">
                                      <p>🎯 Reliability: {dhiData.reliability}% (×0.4)</p>
                                      <p>📅 Planning: {dhiData.planning}% (×0.3)</p>
                                      <p>⚡ Flow: {dhiData.flow}% (×0.3)</p>
                                    </div>
                                    <p className="text-xs text-blue-400 mt-2">Click para ver diagnóstico</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Capacity Projection Heatmap (Simplified) */}
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-violet-500" />
                      Proyección de Capacidad
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-violet-500 cursor-help hover:text-violet-700" />
                          </TooltipTrigger>
                          <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-md p-4" side="right">
                            <div className="space-y-3 text-sm">
                              <div>
                                <p className="font-bold text-violet-400 mb-1">📊 ¿Qué es este widget?</p>
                                <p className="text-slate-300">Un mapa de calor que muestra cuántas tareas tiene asignadas cada persona por día, proyectando hacia atrás desde las fechas de vencimiento.</p>
                              </div>
                              
                              <div className="border-t border-slate-700 pt-2">
                                <p className="font-bold text-violet-400 mb-1">🎯 ¿Para qué sirve?</p>
                                <p className="text-slate-300">Detectar "choques de trenes" (días con demasiadas tareas concentradas) ANTES de que ocurran, permitiendo redistribuir carga o renegociar fechas a tiempo.</p>
                              </div>
                              
                              <div className="border-t border-slate-700 pt-2">
                                <p className="font-bold text-violet-400 mb-1">🎨 Código de colores:</p>
                                <div className="grid grid-cols-2 gap-1 text-xs">
                                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-400"></div> <span>1-2 tareas: Manejable</span></div>
                                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-400"></div> <span>3-5 tareas: Ajustado</span></div>
                                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-400"></div> <span>6+ tareas: Sobrecarga</span></div>
                                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500"></div> <span>Punto azul: En progreso</span></div>
                                </div>
                              </div>
                              
                              <div className="border-t border-slate-700 pt-2">
                                <p className="font-bold text-violet-400 mb-1">⏱️ Lead Time (0-3 días):</p>
                                <p className="text-slate-300 text-xs">Días de anticipación para trabajar antes del due date. Ej: Si Lead Time = 2d y el due date es viernes, la tarea aparece desde el miércoles. <strong>No cuenta fines de semana.</strong></p>
                              </div>
                              
                              <div className="border-t border-slate-700 pt-2">
                                <p className="font-bold text-violet-400 mb-1">📅 Línea de tiempo:</p>
                                <p className="text-slate-300 text-xs">Muestra 7 días pasados + HOY (columna violeta) + 14 días futuros. Los días pasados aparecen con colores más tenues.</p>
                              </div>
                              
                              <div className="border-t border-slate-700 pt-2">
                                <p className="font-bold text-violet-400 mb-1">👆 Interacción:</p>
                                <p className="text-slate-300 text-xs">Haz clic en cualquier celda con número para ver el detalle: lista de tareas, su estado actual y link directo a ClickUp.</p>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </CardTitle>
                    <CardDescription>
                      Detecta &quot;choques de trenes&quot; antes de que ocurran
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 whitespace-nowrap">Lead Time:</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setLeadTimeDays(Math.max(0, leadTimeDays - 1))}
                        disabled={leadTimeDays === 0}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-sm font-bold bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        −
                      </button>
                      <Badge className="bg-violet-100 text-violet-700 min-w-[40px] justify-center">
                        {leadTimeDays}d
                      </Badge>
                      <button
                        onClick={() => setLeadTimeDays(Math.min(3, leadTimeDays + 1))}
                        disabled={leadTimeDays === 3}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-sm font-bold bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {capacityProjection.users.length > 0 ? (
                  <div className="space-y-3">
                    {/* Heatmap Grid */}
                    <div className="overflow-x-auto">
                      <div className="inline-block min-w-full">
                        {/* Day headers */}
                        <div className="flex mb-1">
                          <div className="w-20 flex-shrink-0" />
                          {capacityProjection.days.map((day) => (
                            <div 
                              key={day.dateStr} 
                              className={`w-8 flex flex-col items-center justify-center text-[10px] font-medium ${
                                day.isToday ? 'text-violet-600' : 
                                day.isPast ? 'text-slate-400' :
                                day.isWeekend ? 'text-slate-400' : 'text-slate-600'
                              }`}
                            >
                              {day.isToday && (
                                <div className="text-[8px] font-bold text-violet-600 bg-violet-100 px-1 rounded mb-0.5">
                                  HOY
                                </div>
                              )}
                              <div className="text-center leading-tight">
                                <div>{day.dayLabel.split(' ')[0]}</div>
                                <div className={`font-bold ${day.isToday ? 'text-violet-600' : ''}`}>{day.dayLabel.split(' ')[1]}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* User rows with heatmap cells */}
                        {capacityProjection.users.map((user) => (
                          <div key={user.username} className="flex items-center mb-1">
                            <div className="w-20 flex-shrink-0 text-xs font-medium text-slate-700 truncate pr-2" title={user.username}>
                              {user.shortName}
                            </div>
                            {capacityProjection.days.map((day) => {
                              const dayTasks = user.dayLoad[day.dateStr] || [];
                              const count = dayTasks.length;
                              const hasInProgress = dayTasks.some(t => t.isInProgress);
                              
                              // Determine cell color based on load
                              let cellBg = "bg-slate-100";
                              let textColor = "text-slate-400";
                              
                              if (count >= 6) {
                                cellBg = day.isPast ? "bg-red-100" : "bg-red-200 hover:bg-red-300";
                                textColor = day.isPast ? "text-red-500" : "text-red-800";
                              } else if (count >= 3) {
                                cellBg = day.isPast ? "bg-amber-100" : "bg-amber-200 hover:bg-amber-300";
                                textColor = day.isPast ? "text-amber-500" : "text-amber-800";
                              } else if (count >= 1) {
                                cellBg = day.isPast ? "bg-green-100" : "bg-green-200 hover:bg-green-300";
                                textColor = day.isPast ? "text-green-500" : "text-green-800";
                              } else if (day.isPast) {
                                cellBg = "bg-slate-50";
                              }
                              
                              // Add today column highlight
                              const todayHighlight = day.isToday ? "ring-2 ring-violet-400 ring-inset" : "";
                              // Add "in progress" indicator (blue dot)
                              const inProgressIndicator = hasInProgress && !day.isPast;
                              
                              return (
                                <div
                                  key={day.dateStr}
                                  className={`w-8 h-8 rounded-sm flex items-center justify-center text-xs font-medium cursor-pointer transition-all relative ${cellBg} ${textColor} ${todayHighlight} ${day.isWeekend && count === 0 ? 'bg-slate-50' : ''} ${count > 0 && !day.isPast ? 'hover:scale-110 hover:shadow-md hover:z-10' : ''}`}
                                  onClick={() => count > 0 && setSelectedCapacityCell({
                                    username: user.username,
                                    shortName: user.shortName,
                                    date: day.dateStr,
                                    dayLabel: day.dayLabel,
                                    tasks: dayTasks,
                                    count
                                  })}
                                >
                                  {count > 0 ? count : ""}
                                  {inProgressIndicator && (
                                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-500 rounded-full border border-white" title="Tiene tareas en progreso" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-3 text-xs pt-2 border-t">
                      <span className="text-slate-500">Carga:</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-sm bg-slate-100" />
                        <span className="text-slate-500">0</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-sm bg-green-200" />
                        <span className="text-slate-600">1-2</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-sm bg-amber-200" />
                        <span className="text-slate-600">3-5</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded-sm bg-red-200" />
                        <span className="text-slate-600">6+</span>
                      </div>
                      <span className="text-slate-400 ml-2">| Clic para ver detalle</span>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-slate-400">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No hay tareas con due dates en los próximos 14 días</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Capacity Cell Detail Modal */}
            <Dialog open={!!selectedCapacityCell} onOpenChange={() => setSelectedCapacityCell(null)}>
              <DialogContent className="max-w-md bg-white rounded-lg !grid-rows-[auto_1fr] overflow-hidden">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-violet-500" />
                    Carga Proyectada
                  </DialogTitle>
                  <DialogDescription>
                    {selectedCapacityCell?.shortName} - {selectedCapacityCell?.dayLabel}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 overflow-hidden">
                  <div className="flex items-center gap-2">
                    <Badge className={`${
                      (selectedCapacityCell?.count || 0) >= 6 ? 'bg-red-100 text-red-700' :
                      (selectedCapacityCell?.count || 0) >= 3 ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {selectedCapacityCell?.count} tarea{selectedCapacityCell?.count !== 1 ? 's' : ''}
                    </Badge>
                    {(selectedCapacityCell?.count || 0) >= 6 && (
                      <span className="text-xs text-red-600">⚠️ Sobrecarga detectada</span>
                    )}
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {selectedCapacityCell?.tasks.map((task, idx) => (
                      <div key={idx} className={`text-sm p-2 rounded flex items-start gap-2 ${task.isInProgress ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'}`}>
                        <span className="text-slate-400 font-mono text-xs">{idx + 1}.</span>
                        <div className="flex-1 min-w-0">
                          {task.url ? (
                            <a 
                              href={task.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline hover:text-blue-800 block truncate"
                              title={task.name}
                            >
                              {task.name}
                            </a>
                          ) : (
                            <span className="text-slate-700 block truncate" title={task.name}>{task.name}</span>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-[10px] px-1.5 py-0 ${
                              task.isInProgress ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'
                            }`}>
                              {task.status}
                            </Badge>
                            {task.url && (
                              <a 
                                href={task.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[10px] text-slate-400 hover:text-blue-600 flex items-center gap-0.5"
                              >
                                <ExternalLink className="h-2.5 w-2.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {(selectedCapacityCell?.count || 0) >= 3 && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                      <p className="text-amber-800">
                        💡 <strong>Tip:</strong> Considera renegociar fechas o redistribuir tareas.
                      </p>
                    </div>
                  )}
                  
                  <Button variant="outline" className="w-full" onClick={() => setSelectedCapacityCell(null)}>
                    Cerrar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Zombie List Table */}
            {sprintKPIs.zombieCount > 0 && (
              <Card className="border-amber-200">
                <CardHeader className="bg-amber-50/50">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                    <Skull className="h-5 w-5" />
                    The Zombie List
                    <Badge className="bg-amber-100 text-amber-700 ml-2">{sprintKPIs.zombieCount}</Badge>
                  </CardTitle>
                  <CardDescription className="text-amber-700">
                    Tareas "In Progress" estancadas por más de 30 días. Posibles bombas de tiempo que requieren atención.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarea</TableHead>
                        <TableHead>Asignado</TableHead>
                        <TableHead>Creado por</TableHead>
                        <TableHead className="text-center">Días Activo</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-center">Acción</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sprintKPIs.zombieTasks.map((task) => (
                        <TableRow key={task.task_id} className="hover:bg-amber-50/50">
                          <TableCell className="max-w-xs">
                            <div className="flex items-center gap-2">
                              {task.url ? (
                                <a 
                                  href={task.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline truncate font-medium"
                                >
                                  {task.task_name}
                                </a>
                              ) : (
                                <span className="truncate">{task.task_name}</span>
                              )}
                            </div>
                            {task.space_name && (
                              <p className="text-xs text-slate-500 truncate">📁 {task.space_name}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-medium">
                                {task.username?.charAt(0)}
                              </div>
                              <span className="text-sm">{task.username?.split(" ")[0]}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {task.created_by ? (
                              <span className="text-sm text-slate-600">{task.created_by}</span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={`${
                              (task.task_age_days || 0) > 60 ? 'bg-red-100 text-red-700' : 
                              (task.task_age_days || 0) > 45 ? 'bg-amber-100 text-amber-700' : 
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {Math.round(task.task_age_days || 0)} días
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {task.due_date ? (
                              <span className={`text-sm ${task.urgency_status === 'Vencida' ? 'text-red-600' : 'text-slate-600'}`}>
                                {new Date(task.due_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-sm">Sin fecha</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                              onClick={() => {
                                if (task.url) {
                                  window.open(task.url, '_blank');
                                }
                                toast.info("🔔 Nudge enviado", {
                                  description: `Revisar tarea de ${task.username?.split(" ")[0]}`
                                });
                              }}
                            >
                              <Bell className="h-3.5 w-3.5 mr-1" />
                              Nudge
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {sprintKPIs.zombieCount === 0 && (
              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="py-8 text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <p className="font-medium text-emerald-800">¡Sin Zombies! 🎉</p>
                  <p className="text-sm text-emerald-600 mt-1">
                    No hay tareas &quot;In Progress&quot; estancadas por más de 30 días.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ================================================== */}
          {/* CLIENT STRATEGY TAB - Holistic Client View */}
          {/* ================================================== */}
          <TabsContent value="client-strategy" className="space-y-6">
            {/* Header Banner */}
            <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-indigo-100">
                    <Grid3X3 className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-indigo-800">Vista Estratégica de Clientes</p>
                    <p className="text-sm text-indigo-700 mt-1">
                      Balance de carga entre clientes, estabilidad de planificación y detección de riesgos de concentración.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Firefighting Index + Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Firefighting Index Donut */}
              <Card className="md:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Flame className="h-5 w-5 text-orange-500" />
                    Firefighting Index
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-xs">
                          <div className="space-y-2 text-sm">
                            <p className="font-semibold">¿Qué mide?</p>
                            <p>Analiza las tareas &quot;In Progress&quot; según su fecha de creación:</p>
                            <p><span className="text-red-400">• Reactivo:</span> Creadas en las últimas 48h (apagando incendios)</p>
                            <p><span className="text-green-400">• Planificado:</span> Creadas hace más de 48h (trabajo programado)</p>
                            <p className="font-semibold mt-2">¿Por qué importa?</p>
                            <p>Un alto % reactivo indica que el equipo está constantemente &quot;apagando fuegos&quot; en lugar de ejecutar trabajo planificado, lo que genera estrés y reduce la calidad.</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                  <CardDescription>
                    Reactividad del equipo en tareas &quot;In Progress&quot;
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {firefightingIndex.total > 0 ? (
                    <div className="space-y-4">
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: "Reactivo", value: firefightingIndex.reactive, fill: "#ef4444" },
                                { name: "Planificado", value: firefightingIndex.planned, fill: "#22c55e" }
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={70}
                              paddingAngle={2}
                              dataKey="value"
                            >
                            </Pie>
                            <RechartsTooltip 
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <div className="bg-white p-2 rounded shadow-lg border text-sm">
                                      <p className="font-medium">{payload[0].name}</p>
                                      <p>{payload[0].value} tareas ({Math.round((payload[0].value / firefightingIndex.total) * 100)}%)</p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-slate-800">{firefightingIndex.reactivePercent}%</p>
                        <p className="text-sm text-slate-500">Reactivo</p>
                      </div>
                      <div className="flex justify-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-red-500" />
                          <span>Reactivo ({firefightingIndex.reactive})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-green-500" />
                          <span>Planificado ({firefightingIndex.planned})</span>
                        </div>
                      </div>
                      {firefightingIndex.isHighReactivity && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            <span><strong>⚠️ Alta inestabilidad:</strong> El equipo está operando en modo reactivo.</span>
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-slate-400">
                      <p className="text-sm">Sin tareas en progreso</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Client Neglect Radar */}
              <Card className="md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Client Neglect Radar
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-xs">
                          <div className="space-y-2 text-sm">
                            <p className="font-semibold">¿Qué mide?</p>
                            <p>Calcula el promedio de antigüedad (días desde creación) de las tareas activas por cada cliente/espacio.</p>
                            <p><span className="text-blue-400">• Barras azules:</span> Promedio menor a 45 días (saludable)</p>
                            <p><span className="text-red-400">• Barras rojas:</span> Promedio mayor a 45 días (cliente desatendido)</p>
                            <p className="font-semibold mt-2">¿Por qué importa?</p>
                            <p>Clientes con tickets muy viejos pueden sentirse ignorados. Este radar ayuda a identificar dónde enfocar esfuerzos para mejorar la satisfacción del cliente.</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                  <CardDescription>
                    Promedio de antigüedad de tareas por cliente. Barras rojas = más de 45 días promedio.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {clientNeglectData.length > 0 ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={clientNeglectData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                          <XAxis type="number" unit=" días" />
                          <YAxis 
                            dataKey="shortSpace" 
                            type="category" 
                            width={120}
                            tick={{ fontSize: 10 }}
                            interval={0}
                          />
                          <RechartsTooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
                                    <p className="font-semibold">{d.space}</p>
                                    <p className="text-slate-600">Promedio: <strong>{d.avgAge} días</strong></p>
                                    <p className="text-slate-500">{d.taskCount} tareas activas</p>
                                    {d.isNeglected && (
                                      <p className="text-red-600 mt-1">⚠️ Cliente desatendido</p>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="avgAge" radius={[0, 4, 4, 0]}>
                            {clientNeglectData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.isNeglected ? "#ef4444" : "#3b82f6"} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-72 flex items-center justify-center text-slate-400">
                      <p className="text-sm">Sin datos de clientes</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Resource Swarm Matrix (Scatter Plot with 4 Quadrants) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-violet-500" />
                  The Resource Swarm Matrix
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-sm">
                        <div className="space-y-2 text-sm">
                          <p className="font-semibold">¿Qué mide?</p>
                          <p>Muestra la relación entre volumen de trabajo (tareas) e involucramiento del equipo (personas asignadas) por cliente.</p>
                          <p className="font-semibold mt-2">Los 4 Cuadrantes:</p>
                          <p><span className="text-green-400">↗️ Strategic:</span> Cuentas grandes bien atendidas</p>
                          <p><span className="text-amber-400">↖️ Crowded Kitchen:</span> Mucha gente, poco trabajo = ineficiencia</p>
                          <p><span className="text-red-400">↘️ Burnout Zone:</span> Mucho trabajo, poca gente = riesgo</p>
                          <p><span className="text-blue-400">↙️ Maintenance:</span> Piloto automático, eficiente</p>
                          <p className="font-semibold mt-2">Tamaño de burbuja:</p>
                          <p>Proporcional a tareas urgentes (High/Urgent)</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
                <CardDescription>
                  Densidad de recursos por cliente: Volumen de tareas vs Personas involucradas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {resourceSwarmData.clients.length > 0 ? (
                  <div className="space-y-4">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            type="number" 
                            dataKey="x" 
                            name="Tareas" 
                            label={{ value: "Volumen (Tareas Activas)", position: "bottom", offset: 0, fontSize: 11 }}
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis 
                            type="number" 
                            dataKey="y" 
                            name="Personas"
                            label={{ value: "Personas", angle: -90, position: "insideLeft", fontSize: 11 }}
                            tick={{ fontSize: 10 }}
                          />
                          <ZAxis type="number" dataKey="z" range={[50, 400]} />
                          
                          {/* Quadrant reference lines */}
                          <ReferenceLine 
                            x={resourceSwarmData.medianX} 
                            stroke="#94a3b8" 
                            strokeDasharray="5 5" 
                            strokeWidth={2}
                          />
                          <ReferenceLine 
                            y={resourceSwarmData.medianY} 
                            stroke="#94a3b8" 
                            strokeDasharray="5 5" 
                            strokeWidth={2}
                          />
                          
                          <RechartsTooltip
                            cursor={{ strokeDasharray: "3 3" }}
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 rounded-lg shadow-lg border text-sm max-w-xs">
                                    <p className="font-bold text-slate-800">{d.name}</p>
                                    <div className="mt-2 space-y-1">
                                      <p className="text-slate-600">
                                        <strong>Carga:</strong> {d.x} tareas activas
                                      </p>
                                      <p className="text-slate-600">
                                        <strong>Equipo:</strong> {d.y} persona{d.y !== 1 ? "s" : ""}
                                      </p>
                                      {d.userList.length > 0 && (
                                        <p className="text-xs text-slate-500">
                                          {d.userList.map(u => u.split(" ")[0]).join(", ")}
                                          {d.hasMoreUsers && "..."}
                                        </p>
                                      )}
                                      {d.urgentTasks > 0 && (
                                        <p className="text-red-600">
                                          <strong>Urgentes:</strong> {d.urgentTasks}
                                        </p>
                                      )}
                                    </div>
                                    <div className="mt-2 pt-2 border-t">
                                      <Badge 
                                        className="text-xs" 
                                        style={{ backgroundColor: d.quadrantColor + "20", color: d.quadrantColor }}
                                      >
                                        {d.quadrantLabel}
                                      </Badge>
                                      <p className="text-xs text-slate-500 mt-1">
                                        💡 {d.action}
                                      </p>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          
                          <Scatter 
                            name="Clientes" 
                            data={resourceSwarmData.clients}
                            fill="#8884d8"
                          >
                            {resourceSwarmData.clients.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.quadrantColor}
                                fillOpacity={0.7}
                                stroke={entry.quadrantColor}
                                strokeWidth={2}
                              />
                            ))}
                          </Scatter>
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Quadrant Legend */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="flex items-center gap-2 p-2 rounded bg-green-50 border border-green-200">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <div>
                          <p className="font-medium text-green-800">Strategic Accounts</p>
                          <p className="text-green-600">Alto volumen, mucha gente</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2 rounded bg-amber-50 border border-amber-200">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <div>
                          <p className="font-medium text-amber-800">Crowded Kitchen</p>
                          <p className="text-amber-600">Bajo volumen, mucha gente</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-200">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <div>
                          <p className="font-medium text-red-800">Burnout Zone</p>
                          <p className="text-red-600">Alto volumen, poca gente</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2 rounded bg-blue-50 border border-blue-200">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <div>
                          <p className="font-medium text-blue-800">Maintenance Mode</p>
                          <p className="text-blue-600">Bajo volumen, poca gente</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-slate-400">
                    <p className="text-sm">Sin datos para el análisis</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Concentration Matrix (Heatmap) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Grid3X3 className="h-5 w-5 text-indigo-500" />
                  Client Concentration Matrix
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-xs">
                        <div className="space-y-2 text-sm">
                          <p className="font-semibold">¿Qué mide?</p>
                          <p>Muestra cuántas tareas activas (To Do + In Progress) tiene cada persona en cada cliente. Pasa el mouse sobre las celdas para ver el detalle.</p>
                          <p><span className="text-blue-300">• Azul claro (1-2):</span> Carga balanceada</p>
                          <p><span className="text-blue-500">• Azul medio (3-4):</span> Carga moderada</p>
                          <p><span className="text-red-400">• Rojo (5+):</span> Riesgo de concentración</p>
                          <p className="font-semibold mt-2">¿Por qué importa?</p>
                          <p>Detecta &quot;silos&quot; donde una persona es el único punto de contacto con un cliente. Si esa persona se ausenta, el cliente queda desatendido. Idealmente, cada cliente debería tener al menos 2 personas asignadas.</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
                <CardDescription>
                  Distribución de tareas activas (To Do + In Progress) por usuario y cliente. 
                  <span className="ml-2 inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-200" /> 1-2
                    <span className="w-3 h-3 rounded bg-blue-400 ml-2" /> 3-4
                    <span className="w-3 h-3 rounded bg-red-500 ml-2" /> 5+ (riesgo)
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {clientConcentrationMatrix.users.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className="text-left py-1 px-2 bg-slate-50 font-medium text-slate-600 sticky left-0 z-10 text-xs">Usuario</th>
                          {clientConcentrationMatrix.spaces.map((spaceInfo) => (
                            <th 
                              key={spaceInfo.name} 
                              className="bg-slate-50 text-center p-0"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="w-7 h-7 mx-auto flex items-center justify-center text-[9px] font-semibold text-slate-500 cursor-help hover:bg-indigo-100 hover:text-indigo-700 rounded transition-colors">
                                      {spaceInfo.initials}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="!bg-slate-900 !text-white border border-slate-700">
                                    <p className="text-sm font-medium">{spaceInfo.name}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </th>
                          ))}
                          <th className="py-1 px-1 bg-slate-50 font-medium text-slate-600 text-center text-xs">Σ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientConcentrationMatrix.users.map((user, idx) => (
                          <tr key={user.username} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                            <td className="py-1 px-2 font-medium text-slate-700 sticky left-0 z-10 bg-inherit text-xs whitespace-nowrap">
                              {user.shortName}
                            </td>
                            {clientConcentrationMatrix.spaces.map(spaceInfo => {
                              const value = user[spaceInfo.name] || 0;
                              return (
                                <td key={spaceInfo.name} className="p-0 text-center">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div 
                                          className={`mx-auto w-6 h-6 rounded flex items-center justify-center text-[10px] font-medium ${getHeatmapColor(value)} ${getHeatmapTextColor(value)} cursor-help transition-transform hover:scale-110`}
                                        >
                                          {value > 0 ? value : ""}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="!bg-slate-900 !text-white border border-slate-700">
                                        <p className="text-sm">
                                          <strong>{user.username}</strong> tiene <strong>{value}</strong> tareas en <strong>{spaceInfo.name}</strong>
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </td>
                              );
                            })}
                            <td className="py-1 px-1 text-center font-bold text-slate-800 text-xs">
                              {user.total}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-400">
                    <Grid3X3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Sin datos para mostrar la matriz</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ================================================== */}
            {/* BUSINESS INTELLIGENCE SECTION */}
            {/* ================================================== */}
            
            {/* Panic Index + SLA Equity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* The Panic Index */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertOctagon className="h-5 w-5 text-red-500" />
                    The Panic Index
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-xs">
                          <div className="space-y-2 text-sm">
                            <p className="font-semibold">¿Qué mide?</p>
                            <p>Detecta clientes que abusan de prioridades altas. Calcula: (Urgent + High) / Total tareas activas.</p>
                            <p><span className="text-red-400">• Rojo (&gt;50%):</span> &quot;Toxic Demand&quot; - Todo es urgente = nada es urgente</p>
                            <p><span className="text-green-400">• Verde (&lt;10%):</span> &quot;Healthy Flow&quot; - Priorización saludable</p>
                            <p className="font-semibold mt-2">¿Por qué importa?</p>
                            <p>Clientes con alto &quot;pánico&quot; generan estrés innecesario y dificultan la planificación. Pueden necesitar capacitación sobre cómo priorizar o una conversación sobre expectativas.</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                  <CardDescription>
                    Madurez del cliente: % de tareas marcadas como Urgent o High
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {panicIndexData.length > 0 ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={panicIndexData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                          <XAxis type="number" domain={[0, 100]} unit="%" />
                          <YAxis 
                            dataKey="shortSpace" 
                            type="category" 
                            width={130}
                            tick={{ fontSize: 10 }}
                            interval={0}
                          />
                          <RechartsTooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
                                    <p className="font-semibold">{d.space}</p>
                                    <p className="text-slate-600">Pánico: <strong>{d.panicPercent}%</strong></p>
                                    <p className="text-xs text-slate-500">Urgent: {d.urgent} | High: {d.high} | Total: {d.total}</p>
                                    <p className={`mt-1 text-xs font-medium ${
                                      d.status === "toxic" ? "text-red-600" : 
                                      d.status === "healthy" ? "text-green-600" : "text-slate-600"
                                    }`}>
                                      {d.status === "toxic" ? "⚠️ Toxic Demand" : 
                                       d.status === "healthy" ? "✅ Healthy Flow" : "📊 Normal"}
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="panicPercent" radius={[0, 4, 4, 0]}>
                            {panicIndexData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.status === "toxic" ? "#ef4444" : 
                                      entry.status === "healthy" ? "#22c55e" : "#f59e0b"} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-72 flex items-center justify-center text-slate-400">
                      <p className="text-sm">Sin datos de prioridades</p>
                    </div>
                  )}
                  <div className="flex justify-center gap-4 mt-2 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-red-500" />
                      <span>&gt;50% Toxic</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-amber-500" />
                      <span>10-50% Normal</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-green-500" />
                      <span>&lt;10% Healthy</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SLA Equity Radar */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-500" />
                    SLA Equity Radar
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-xs">
                          <div className="space-y-2 text-sm">
                            <p className="font-semibold">¿Qué mide?</p>
                            <p>Compara el tiempo promedio de tareas de cada cliente vs el promedio global del equipo.</p>
                            <p><span className="text-red-400">• Derecha (+15 días):</span> &quot;Neglected&quot; - Cliente desatendido, riesgo de churn</p>
                            <p><span className="text-blue-400">• Izquierda (-15 días):</span> &quot;Gold Plating&quot; - Posible sobre-servicio</p>
                            <p className="font-semibold mt-2">¿Por qué importa?</p>
                            <p>Revela sesgos inconscientes: ¿Priorizamos a los clientes &quot;ruidosos&quot; sobre los silenciosos? Los clientes silenciosos descuidados son los que abandonan sin avisar.</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                  <CardDescription>
                    Desviación del promedio global ({slaEquityData.globalAvg} días) por cliente
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {slaEquityData.clients.length > 0 ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={slaEquityData.clients.slice(0, 12)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                          <XAxis 
                            type="number" 
                            domain={['dataMin - 10', 'dataMax + 10']}
                            tickFormatter={(v) => v > 0 ? `+${v}` : v}
                          />
                          <YAxis 
                            dataKey="shortSpace" 
                            type="category" 
                            width={130}
                            tick={{ fontSize: 10 }}
                            interval={0}
                          />
                          <ReferenceLine x={0} stroke="#64748b" strokeWidth={2} />
                          <RechartsTooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
                                    <p className="font-semibold">{d.space}</p>
                                    <p className="text-slate-600">Promedio: <strong>{d.avgAge} días</strong></p>
                                    <p className="text-slate-500">Desviación: <strong className={d.deviation > 0 ? "text-red-600" : "text-blue-600"}>
                                      {d.deviation > 0 ? "+" : ""}{d.deviation} días
                                    </strong></p>
                                    <p className="text-xs text-slate-400">{d.taskCount} tareas</p>
                                    <p className={`mt-1 text-xs font-medium ${
                                      d.status === "neglected" ? "text-red-600" : 
                                      d.status === "goldplating" ? "text-blue-600" : "text-slate-600"
                                    }`}>
                                      {d.status === "neglected" ? "⚠️ Neglected (Riesgo Churn)" : 
                                       d.status === "goldplating" ? "💎 Gold Plating" : "✅ Balanced"}
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="deviation" radius={[0, 4, 4, 0]}>
                            {slaEquityData.clients.slice(0, 12).map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.status === "neglected" ? "#ef4444" : 
                                      entry.status === "goldplating" ? "#3b82f6" : "#22c55e"} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-72 flex items-center justify-center text-slate-400">
                      <p className="text-sm">Sin datos de SLA</p>
                    </div>
                  )}
                  <div className="flex justify-center gap-4 mt-2 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-red-500" />
                      <span>Neglected (+15d)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-green-500" />
                      <span>Balanced</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-blue-500" />
                      <span>Gold Plating (-15d)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Demand Generators Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-500" />
                  Top Demand Generators
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="!bg-slate-900 !text-white border border-slate-700 max-w-xs">
                        <div className="space-y-2 text-sm">
                          <p className="font-semibold">¿Qué mide?</p>
                          <p>Identifica quién está creando tickets y la calidad de sus solicitudes.</p>
                          <p><strong>Zombie Rate:</strong> % de tickets que terminan estancados más de 30 días en &quot;In Progress&quot;.</p>
                          <p className="font-semibold mt-2">¿Por qué importa?</p>
                          <p>Un solicitante con alto volumen y alta tasa de zombies puede estar generando &quot;basura&quot; en el sistema: tickets mal definidos, sin contexto suficiente, o de bajo valor. Candidato para capacitación o revisión de proceso.</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
                <CardDescription>
                  Calidad de la demanda: Quién genera más tickets y cuántos terminan estancados
                </CardDescription>
              </CardHeader>
              <CardContent>
                {demandGeneratorsData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Solicitante</TableHead>
                          <TableHead className="text-center">Tickets Creados</TableHead>
                          <TableHead className="text-center">Zombies</TableHead>
                          <TableHead className="text-center">Zombie Rate</TableHead>
                          <TableHead className="text-center">Clientes</TableHead>
                          <TableHead className="text-center">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {demandGeneratorsData.map((requester, idx) => (
                          <TableRow key={requester.creator} className={requester.isProblematic ? "bg-red-50/50" : ""}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                                  requester.isProblematic ? "bg-red-500" : "bg-indigo-500"
                                }`}>
                                  {requester.creator.charAt(0).toUpperCase()}
                                </div>
                                <span className="font-medium text-sm" title={requester.creator}>
                                  {requester.shortCreator}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-slate-100 text-slate-700">
                                {requester.ticketsCreated}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {requester.zombies > 0 ? (
                                <Badge className="bg-orange-100 text-orange-700">
                                  {requester.zombies}
                                </Badge>
                              ) : (
                                <span className="text-slate-400">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={`${
                                requester.zombieRate > 30 ? "bg-red-100 text-red-700" :
                                requester.zombieRate > 15 ? "bg-amber-100 text-amber-700" :
                                "bg-green-100 text-green-700"
                              }`}>
                                {requester.zombieRate}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center text-sm text-slate-600">
                              {requester.clientsCount}
                            </TableCell>
                            <TableCell className="text-center">
                              {requester.isProblematic ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge className="bg-red-100 text-red-700 cursor-help">
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Revisar
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent className="!bg-slate-900 !text-white border border-slate-700">
                                      <p className="text-sm">Alto volumen + Alta tasa de zombies = Candidato a capacitación</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  OK
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-400">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Sin datos de solicitantes</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ================================================== */}
          {/* PIPELINE GOVERNANCE TAB - Managerial View */}
          {/* ================================================== */}
          <TabsContent value="pipeline-governance" className="space-y-6">
            {/* Filter for this tab */}
            <Card className="bg-slate-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                    <input
                      type="checkbox"
                      id="excludeWaiting"
                      checked={excludeWaitingStatuses}
                      onChange={(e) => setExcludeWaitingStatuses(e.target.checked)}
                      className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <label htmlFor="excludeWaiting" className="text-sm text-amber-800 cursor-pointer">
                      Excluir tareas en revisión/espera
                    </label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-3.5 w-3.5 text-amber-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-xs">
                            Excluye: ready for review, waiting for feedback, waiting for review, send ok to enovia
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </CardContent>
            </Card>
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Target className="h-5 w-5 text-orange-500" />
            Panel de Salud Operativa
          </h2>
          
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Total Tickets Activos"
              value={kpis.total}
              icon={BarChart3}
              color="border-l-blue-500"
            />
            <KPICard
              title="Tickets Vencidos"
              value={`${kpis.overduePercent}%`}
              subtitle={`${kpis.overdue} tickets`}
              icon={AlertTriangle}
              color="border-l-red-500"
            />
            <KPICard
              title="Sin Fecha Asignada"
              value={`${kpis.noDatePercent}%`}
              subtitle={`${kpis.noDate} tickets`}
              icon={Calendar}
              color="border-l-slate-400"
            />
            <KPICard
              title="Sin Priorizar"
              value={`${kpis.noPriorityPercent}%`}
              subtitle="Prioridad normal/vacía"
              icon={Clock}
              color="border-l-amber-500"
            />
          </div>

          {/* Status Distribution Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Distribución por Estado</CardTitle>
              <CardDescription>Identifica cuellos de botella en el flujo de trabajo</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={150}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      formatter={(value) => [value, "Tickets"]}
                      contentStyle={{ borderRadius: 8 }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Panel 2: Workload Balance */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-500" />
            Balance de Carga por Persona
          </h2>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Carga de Trabajo por Usuario</CardTitle>
              <CardDescription>
                Haz clic en un usuario para ver sus tareas vencidas. Colores indican urgencia.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={userChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={100}
                      tick={{ fontSize: 11 }}
                    />
                    <RechartsTooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
                              <p className="font-semibold mb-2">{data.fullName}</p>
                              <p className="text-red-600">Vencidas: {data.Vencida}</p>
                              <p className="text-slate-500">Sin fecha: {data["Sin fecha"]}</p>
                              <p className="text-amber-600">Próx. 7 días: {data["Próximos 7 días"]}</p>
                              <p className="text-green-600">8-30 días: {data["Próximos 8-30 días"]}</p>
                              <p className="text-blue-600">+30 días: {data["Más de 30 días"]}</p>
                              <p className="font-semibold mt-2 border-t pt-2">Total: {data.total}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Vencida" stackId="a" fill={URGENCY_COLORS["Vencida"]} 
                         onClick={(data) => handleUserClick(data.fullName)} cursor="pointer" />
                    <Bar dataKey="Sin fecha" stackId="a" fill={URGENCY_COLORS["Sin fecha"]} 
                         onClick={(data) => handleUserClick(data.fullName)} cursor="pointer" />
                    <Bar dataKey="Próximos 7 días" stackId="a" fill={URGENCY_COLORS["Próximos 7 días"]} 
                         onClick={(data) => handleUserClick(data.fullName)} cursor="pointer" />
                    <Bar dataKey="Próximos 8-30 días" stackId="a" fill={URGENCY_COLORS["Próximos 8-30 días"]} 
                         onClick={(data) => handleUserClick(data.fullName)} cursor="pointer" />
                    <Bar dataKey="Más de 30 días" stackId="a" fill={URGENCY_COLORS["Más de 30 días"]} radius={[0, 4, 4, 0]}
                         onClick={(data) => handleUserClick(data.fullName)} cursor="pointer" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* User Summary Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumen por Usuario</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Especialidad</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">Vencidas</TableHead>
                    <TableHead className="text-center">Sin Fecha</TableHead>
                    <TableHead className="text-center">Edad Prom.</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUserWorkloads.map((user) => (
                    <TableRow key={user.username} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {user.specialty}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center font-semibold">{user.total_tasks}</TableCell>
                      <TableCell className="text-center">
                        {user.overdue_tasks > 0 ? (
                          <Badge className="bg-red-100 text-red-700">{user.overdue_tasks}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {user.no_date_tasks > 0 ? (
                          <Badge className="bg-slate-100 text-slate-600">{user.no_date_tasks}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{user.avg_task_age} días</TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleUserClick(user.username)}
                        >
                          Ver tareas
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Panel 3: Aging & Bottlenecks */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            Envejecimiento y Cuellos de Botella
          </h2>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Edad de Tickets por Especialidad</CardTitle>
              <CardDescription>
                Tamaño de burbuja = cantidad de tickets. Posición horizontal = edad promedio en días.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="avgAge" 
                      name="Edad Promedio" 
                      unit=" días"
                      type="number"
                    />
                    <YAxis 
                      dataKey="specialty" 
                      type="category" 
                      width={180}
                      tick={{ fontSize: 11 }}
                    />
                    <ZAxis dataKey="count" range={[100, 1000]} name="Tickets" />
                    <RechartsTooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
                              <p className="font-semibold">{data.specialty}</p>
                              <p className="text-slate-600">Tickets: {data.count}</p>
                              <p className="text-slate-600">Edad promedio: {data.avgAge} días</p>
                              <p className="text-slate-600">Ticket más viejo: {data.maxAge} días</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter 
                      data={agingScatterData} 
                      fill="#f97316"
                      fillOpacity={0.6}
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Aging Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...agingScatterData]
              .sort((a, b) => b.avgAge - a.avgAge)
              .slice(0, 3)
              .map((spec, idx) => (
                <Card key={spec.specialty} className={`border-l-4 ${idx === 0 ? 'border-l-red-500' : idx === 1 ? 'border-l-amber-500' : 'border-l-blue-500'}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500">{spec.specialty}</p>
                        <p className="text-2xl font-bold text-slate-900">{spec.avgAge} días</p>
                        <p className="text-xs text-slate-500">{spec.count} tickets activos</p>
                      </div>
                      <div className={`p-2 rounded-full ${idx === 0 ? 'bg-red-100' : idx === 1 ? 'bg-amber-100' : 'bg-blue-100'}`}>
                        <Clock className={`h-5 w-5 ${idx === 0 ? 'text-red-500' : idx === 1 ? 'text-amber-500' : 'text-blue-500'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
          </TabsContent>

          {/* ================================================== */}
          {/* RAW DATA TAB - Query and Data Explorer */}
          {/* ================================================== */}
          <TabsContent value="raw-data" className="space-y-6">
            {/* Collapsible Query Section */}
            <Card className="border-slate-200">
              <CardHeader 
                className="cursor-pointer hover:bg-slate-50 transition-colors py-3"
                onClick={() => setShowQueryCode(!showQueryCode)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Code className="h-4 w-4 text-slate-500" />
                    SQL Query (Databricks)
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleCopyQuery(); }}
                      className="h-7 px-2"
                    >
                      {copiedQuery ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                    {showQueryCode ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
              </CardHeader>
              {showQueryCode && (
                <CardContent className="pt-0">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto max-h-64 overflow-y-auto">
                    <code>{WORKLOAD_SQL_QUERY}</code>
                  </pre>
                </CardContent>
              )}
            </Card>

            {/* Filters */}
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-slate-500" />
                    <Label className="text-sm text-slate-600">Filtros:</Label>
                  </div>
                  
                  {/* Search */}
                  <div className="relative flex-1 min-w-[200px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre, tarea, space..."
                      value={rawDataSearch}
                      onChange={(e) => setRawDataSearch(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>

                  {/* Status Filter */}
                  <Select value={rawDataStatus} onValueChange={setRawDataStatus}>
                    <SelectTrigger className="w-40 h-9">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      {uniqueStatuses.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Urgency Filter */}
                  <Select value={rawDataUrgency} onValueChange={setRawDataUrgency}>
                    <SelectTrigger className="w-40 h-9">
                      <SelectValue placeholder="Urgencia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="Vencida">🔴 Vencida</SelectItem>
                      <SelectItem value="Sin fecha">⚪ Sin fecha</SelectItem>
                      <SelectItem value="Próximos 7 días">🟡 Próximos 7 días</SelectItem>
                      <SelectItem value="Próximos 8-30 días">🟢 8-30 días</SelectItem>
                      <SelectItem value="Más de 30 días">🔵 +30 días</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Results count */}
                  <Badge variant="outline" className="ml-auto">
                    {filteredRawData.length} registros
                  </Badge>

                  {/* Clear filters */}
                  {(rawDataSearch || rawDataStatus !== "all" || rawDataUrgency !== "all") && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { 
                        setRawDataSearch(""); 
                        setRawDataStatus("all"); 
                        setRawDataUrgency("all"); 
                      }}
                      className="h-9"
                    >
                      Limpiar
                    </Button>
                  )}

                  {/* Export CSV Button */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleExportCSV}
                    className="h-9 ml-auto gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Data Table */}
            <Card>
              <CardContent className="p-0">
                <div className="max-h-[60vh] overflow-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-[140px]">Usuario</TableHead>
                        <TableHead className="min-w-[250px]">Tarea</TableHead>
                        <TableHead className="w-[100px]">Estado</TableHead>
                        <TableHead className="w-[100px]">Urgencia</TableHead>
                        <TableHead className="w-[80px] text-center">Días</TableHead>
                        <TableHead className="w-[60px] text-center">Ver</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRawData.slice(0, 200).map((task, idx) => (
                        <TableRow 
                          key={`${task.task_id}-${idx}`} 
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => { setSelectedTaskDetail(task); setShowTaskDetailModal(true); }}
                        >
                          <TableCell className="py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-xs font-medium">
                                {task.username?.charAt(0)}
                              </div>
                              <span className="text-sm truncate max-w-[100px]">{task.username?.split(" ")[0]}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <p className="text-sm truncate max-w-[250px]">{task.task_name}</p>
                            {task.space_name && (
                              <p className="text-xs text-slate-400 truncate">📁 {task.space_name}</p>
                            )}
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge variant="outline" className="text-xs font-normal">
                              {task.status_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge className={`text-xs font-normal ${
                              task.urgency_status === 'Vencida' ? 'bg-red-100 text-red-700' :
                              task.urgency_status === 'Sin fecha' ? 'bg-slate-100 text-slate-600' :
                              task.urgency_status === 'Próximos 7 días' ? 'bg-amber-100 text-amber-700' :
                              task.urgency_status === 'Próximos 8-30 días' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {task.urgency_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2 text-center text-sm text-slate-600">
                            {Math.round(task.task_age_days || 0)}
                          </TableCell>
                          <TableCell className="py-2 text-center">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <Eye className="h-4 w-4 text-slate-400" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {filteredRawData.length > 200 && (
                  <div className="p-3 text-center text-sm text-slate-500 border-t bg-slate-50">
                    Mostrando 200 de {filteredRawData.length} registros. Usa los filtros para refinar la búsqueda.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Task Detail Modal for Raw Data */}
      <Dialog open={showTaskDetailModal} onOpenChange={setShowTaskDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-orange-500" />
              Detalle de Tarea
            </DialogTitle>
          </DialogHeader>
          {selectedTaskDetail && (
            <div className="space-y-4">
              {/* Task Name */}
              <div>
                <h3 className="font-semibold text-lg">{selectedTaskDetail.task_name}</h3>
                {selectedTaskDetail.url && (
                  <a 
                    href={selectedTaskDetail.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    Abrir en ClickUp <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>

              {/* Key Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Asignado a</p>
                  <p className="font-medium">{selectedTaskDetail.username}</p>
                  <p className="text-xs text-slate-500">{selectedTaskDetail.email}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Creado por</p>
                  <p className="font-medium">{selectedTaskDetail.created_by || 'N/A'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Estado</p>
                  <Badge variant="outline">{selectedTaskDetail.status_status}</Badge>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Prioridad</p>
                  <Badge className={`${
                    selectedTaskDetail.priority?.toLowerCase() === 'urgent' ? 'bg-red-100 text-red-700' :
                    selectedTaskDetail.priority?.toLowerCase() === 'high' ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {selectedTaskDetail.priority || 'Normal'}
                  </Badge>
                </div>
              </div>

              {/* Dates and Times */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">📅 Due Date</p>
                  <p className={`text-sm font-medium ${selectedTaskDetail.urgency_status === 'Vencida' ? 'text-red-600' : ''}`}>
                    {selectedTaskDetail.due_date 
                      ? new Date(selectedTaskDetail.due_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                      : 'Sin fecha'}
                  </p>
                </div>
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">⏱️ Edad</p>
                  <p className="text-sm font-medium">{Math.round(selectedTaskDetail.task_age_days || 0)} días</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">🚦 Urgencia</p>
                  <Badge className={`text-xs ${
                    selectedTaskDetail.urgency_status === 'Vencida' ? 'bg-red-100 text-red-700' :
                    selectedTaskDetail.urgency_status === 'Sin fecha' ? 'bg-slate-100 text-slate-600' :
                    selectedTaskDetail.urgency_status === 'Próximos 7 días' ? 'bg-amber-100 text-amber-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {selectedTaskDetail.urgency_status}
                  </Badge>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Space:</span>
                  <span className="ml-2">{selectedTaskDetail.space_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Tipo:</span>
                  <span className="ml-2">{selectedTaskDetail.item_type || 'Task'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Especialidad:</span>
                  <span className="ml-2">{selectedTaskDetail.specialty || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Rol:</span>
                  <span className="ml-2">{selectedTaskDetail.role_type || 'N/A'}</span>
                </div>
              </div>

              {/* Time Tracking */}
              {(selectedTaskDetail.estimated_hours || selectedTaskDetail.spent_hours) && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 mb-2">⏰ Time Tracking</p>
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-slate-500">Estimado:</span>
                      <span className="ml-2 font-medium">{selectedTaskDetail.estimated_hours || 0}h</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Registrado:</span>
                      <span className="ml-2 font-medium">{selectedTaskDetail.spent_hours || 0}h</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Tasks Dialog */}
      <Dialog open={showUserTasks} onOpenChange={setShowUserTasks}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-500" />
              Tareas de {selectedUser}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] pr-2">
            {selectedUser && (
              <div className="space-y-3">
                {getUserOverdueTasks(selectedUser).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                    <p>¡Sin tareas vencidas! 🎉</p>
                  </div>
                ) : (
                  getUserOverdueTasks(selectedUser).map((task) => (
                    <div 
                      key={task.task_id}
                      className="p-4 rounded-lg border bg-slate-50 hover:bg-white transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {task.priority && (
                              <Badge className={`text-xs ${
                                task.priority.toLowerCase() === 'urgent' ? 'bg-red-100 text-red-700' :
                                task.priority.toLowerCase() === 'high' ? 'bg-orange-100 text-orange-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {task.priority}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {task.status_status}
                            </Badge>
                          </div>
                          <p className="font-medium text-slate-900 truncate">{task.task_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            {task.space_name && <span>📁 {task.space_name}</span>}
                            {task.due_date && <span>📅 Vencida: {new Date(task.due_date).toLocaleDateString()}</span>}
                            {task.task_age_days && <span>⏱️ {task.task_age_days} días</span>}
                          </div>
                        </div>
                        {task.url && (
                          <a 
                            href={task.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-slate-200 rounded transition-colors"
                          >
                            <ExternalLink className="h-4 w-4 text-slate-500" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sprint User Tasks Detail Dialog */}
      <Dialog open={showSprintUserDetail} onOpenChange={setShowSprintUserDetail}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-500" />
              Tareas de {sprintUserDetail?.username}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[70vh] pr-2">
            {sprintUserDetail && (
              <div className="space-y-6">
                {/* Vencidas Section */}
                {sprintUserDetail.vencidas.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-red-700 flex items-center gap-2 mb-3">
                      🔴 Vencidas ({sprintUserDetail.vencidas.length})
                    </h3>
                    <div className="space-y-2">
                      {sprintUserDetail.vencidas.map(task => (
                        <div key={task.task_id} className="p-3 rounded-lg border border-red-200 bg-red-50/50 hover:bg-red-50">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {task.isStale && (
                                  <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                                    🕐 Stale ({task.daysSinceUpdate}d)
                                  </Badge>
                                )}
                                {task.priority && (
                                  <Badge className="text-xs bg-red-100 text-red-700">{task.priority}</Badge>
                                )}
                                <Badge variant="outline" className="text-xs">{task.status_status}</Badge>
                              </div>
                              {task.url ? (
                                <a href={task.url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-blue-600 hover:underline">
                                  {task.task_name}
                                </a>
                              ) : (
                                <p className="font-medium text-slate-900">{task.task_name}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                {task.space_name && <span>📁 {task.space_name}</span>}
                                {task.due_date && <span>📅 {new Date(task.due_date).toLocaleDateString('es-ES')}</span>}
                                <span>⏱️ {Math.round(task.task_age_days || 0)} días</span>
                                {task.daysSinceUpdate !== null && (
                                  <span className={task.isStale ? 'text-purple-600 font-medium' : ''}>
                                    🔄 Actualizado hace {task.daysSinceUpdate}d
                                  </span>
                                )}
                              </div>
                            </div>
                            {task.url && (
                              <a href={task.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-red-100 rounded">
                                <ExternalLink className="h-4 w-4 text-slate-400" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sin Fecha Section */}
                {sprintUserDetail.sinFecha.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-600 flex items-center gap-2 mb-3">
                      ⚪ Sin Fecha ({sprintUserDetail.sinFecha.length})
                    </h3>
                    <div className="space-y-2">
                      {sprintUserDetail.sinFecha.map(task => (
                        <div key={task.task_id} className="p-3 rounded-lg border border-slate-200 bg-slate-50/50 hover:bg-slate-50">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {task.isStale && (
                                  <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                                    🕐 Stale ({task.daysSinceUpdate}d)
                                  </Badge>
                                )}
                                {task.priority && (
                                  <Badge className="text-xs bg-slate-100 text-slate-700">{task.priority}</Badge>
                                )}
                                <Badge variant="outline" className="text-xs">{task.status_status}</Badge>
                              </div>
                              {task.url ? (
                                <a href={task.url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-blue-600 hover:underline">
                                  {task.task_name}
                                </a>
                              ) : (
                                <p className="font-medium text-slate-900">{task.task_name}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                {task.space_name && <span>📁 {task.space_name}</span>}
                                <span>⏱️ {Math.round(task.task_age_days || 0)} días</span>
                                {task.daysSinceUpdate !== null && (
                                  <span className={task.isStale ? 'text-purple-600 font-medium' : ''}>
                                    🔄 Actualizado hace {task.daysSinceUpdate}d
                                  </span>
                                )}
                              </div>
                            </div>
                            {task.url && (
                              <a href={task.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded">
                                <ExternalLink className="h-4 w-4 text-slate-400" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* A Tiempo Section */}
                {sprintUserDetail.aTiempo.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-emerald-700 flex items-center gap-2 mb-3">
                      🟢 A Tiempo ({sprintUserDetail.aTiempo.length})
                    </h3>
                    <div className="space-y-2">
                      {sprintUserDetail.aTiempo.map(task => (
                        <div key={task.task_id} className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {task.isStale && (
                                  <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                                    🕐 Stale ({task.daysSinceUpdate}d)
                                  </Badge>
                                )}
                                {task.priority && (
                                  <Badge className="text-xs bg-emerald-100 text-emerald-700">{task.priority}</Badge>
                                )}
                                <Badge variant="outline" className="text-xs">{task.status_status}</Badge>
                              </div>
                              {task.url ? (
                                <a href={task.url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-blue-600 hover:underline">
                                  {task.task_name}
                                </a>
                              ) : (
                                <p className="font-medium text-slate-900">{task.task_name}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                {task.space_name && <span>📁 {task.space_name}</span>}
                                {task.due_date && <span>📅 {new Date(task.due_date).toLocaleDateString('es-ES')}</span>}
                                <span>⏱️ {Math.round(task.task_age_days || 0)} días</span>
                                {task.daysSinceUpdate !== null && (
                                  <span className={task.isStale ? 'text-purple-600 font-medium' : ''}>
                                    🔄 Actualizado hace {task.daysSinceUpdate}d
                                  </span>
                                )}
                              </div>
                            </div>
                            {task.url && (
                              <a href={task.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-emerald-100 rounded">
                                <ExternalLink className="h-4 w-4 text-slate-400" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {sprintUserDetail.vencidas.length === 0 && sprintUserDetail.sinFecha.length === 0 && sprintUserDetail.aTiempo.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                    <p>¡Sin tareas asignadas!</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* DHI Diagnostic Modal */}
      <Dialog open={showDhiModal} onOpenChange={(open) => { setShowDhiModal(open); if (!open) setDhiModalTab("overdue"); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-emerald-500" />
              Diagnóstico DHI - {dhiDiagnostic?.username}
            </DialogTitle>
          </DialogHeader>
          {dhiDiagnostic && (
            <div className="space-y-4">
              {/* DHI Score Summary - Compact */}
              <div className="grid grid-cols-4 gap-3">
                <Card className={`border-2 ${getDhiColor(dhiDiagnostic.dhi).border}`}>
                  <CardContent className="py-3 text-center">
                    <p className="text-xs text-slate-500">DHI Total</p>
                    <p className={`text-2xl font-bold ${getDhiColor(dhiDiagnostic.dhi).text}`}>
                      {dhiDiagnostic.dhi}%
                    </p>
                  </CardContent>
                </Card>
                <Card className={dhiModalTab === "overdue" ? "ring-2 ring-red-300" : ""}>
                  <CardContent className="py-3 text-center cursor-pointer" onClick={() => setDhiModalTab("overdue")}>
                    <p className="text-xs text-slate-500">🎯 Reliability</p>
                    <p className={`text-xl font-bold ${dhiDiagnostic.reliability >= 80 ? 'text-emerald-600' : dhiDiagnostic.reliability >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                      {dhiDiagnostic.reliability}%
                    </p>
                    <p className="text-xs text-slate-400">×0.4</p>
                  </CardContent>
                </Card>
                <Card className={dhiModalTab === "nodate" ? "ring-2 ring-slate-300" : ""}>
                  <CardContent className="py-3 text-center cursor-pointer" onClick={() => setDhiModalTab("nodate")}>
                    <p className="text-xs text-slate-500">📅 Planning</p>
                    <p className={`text-xl font-bold ${dhiDiagnostic.planning >= 80 ? 'text-emerald-600' : dhiDiagnostic.planning >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                      {dhiDiagnostic.planning}%
                    </p>
                    <p className="text-xs text-slate-400">×0.3</p>
                  </CardContent>
                </Card>
                <Card className={dhiModalTab === "zombies" ? "ring-2 ring-purple-300" : ""}>
                  <CardContent className="py-3 text-center cursor-pointer" onClick={() => setDhiModalTab("zombies")}>
                    <p className="text-xs text-slate-500">⚡ Flow</p>
                    <p className={`text-xl font-bold ${dhiDiagnostic.flow >= 80 ? 'text-emerald-600' : dhiDiagnostic.flow >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                      {dhiDiagnostic.flow}%
                    </p>
                    <p className="text-xs text-slate-400">×0.3</p>
                  </CardContent>
                </Card>
              </div>

              {/* Perfect Score State */}
              {dhiDiagnostic.dhi === 100 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-emerald-500" />
                  <p className="text-xl font-semibold text-emerald-700">¡Excelente! 🎉</p>
                  <p className="text-slate-600 mt-2">
                    {dhiDiagnostic.username} tiene un DHI perfecto. Sin tareas que afecten negativamente el índice.
                  </p>
                </div>
              ) : (
                /* Tabs for Offender Categories */
                <Tabs value={dhiModalTab} onValueChange={setDhiModalTab} className="space-y-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="overdue" className="flex items-center gap-2 data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
                      🔴 Vencidas
                      <Badge className="bg-red-200 text-red-700 text-xs">{dhiDiagnostic.offenders.overdue.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="nodate" className="flex items-center gap-2 data-[state=active]:bg-slate-200 data-[state=active]:text-slate-700">
                      ⚪ Sin Fecha
                      <Badge className="bg-slate-200 text-slate-700 text-xs">{dhiDiagnostic.offenders.noDate.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="zombies" className="flex items-center gap-2 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                      🧟 Zombies
                      <Badge className="bg-purple-200 text-purple-700 text-xs">{dhiDiagnostic.offenders.zombies.length}</Badge>
                    </TabsTrigger>
                  </TabsList>

                  {/* Tab: Overdue Tasks - Afectan Reliability */}
                  <TabsContent value="overdue" className="space-y-4">
                    {/* Recommendation Card */}
                    <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
                      <CardContent className="py-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-full bg-red-100">
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-red-800 text-sm">Cómo mejorar Reliability</p>
                            <ul className="text-xs text-red-700 mt-2 space-y-1 list-disc list-inside">
                              <li>Revisa las tareas vencidas diariamente en tu standup</li>
                              <li>Re-negocia due dates realistas con stakeholders</li>
                              <li>Si una tarea está bloqueada, actualiza su estado a "Blocked"</li>
                              <li>Prioriza cerrar tareas pequeñas vencidas para reducir el conteo rápidamente</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Task List */}
                    <div className="overflow-y-auto max-h-[40vh] space-y-2 pr-2">
                      {dhiDiagnostic.offenders.overdue.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                          <p>¡Sin tareas vencidas! 🎉</p>
                        </div>
                      ) : (
                        dhiDiagnostic.offenders.overdue.map((task, idx) => (
                          <div key={`${task.task_id}-${idx}`} className="p-3 rounded-lg border border-red-200 bg-red-50/50 hover:bg-red-50">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {task.url ? (
                                  <a href={task.url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-blue-600 hover:underline text-sm">
                                    {task.task_name}
                                  </a>
                                ) : (
                                  <p className="font-medium text-slate-900 text-sm">{task.task_name}</p>
                                )}
                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                  {task.due_date && <span className="text-red-600">📅 {new Date(task.due_date).toLocaleDateString('es-ES')}</span>}
                                  <span>⏱️ {Math.round(task.task_age_days || 0)} días</span>
                                  {task.space_name && <span className="text-slate-400">📁 {task.space_name}</span>}
                                </div>
                              </div>
                              {task.url && (
                                <a href={task.url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-red-100 rounded">
                                  <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>

                  {/* Tab: No Date Tasks - Afectan Planning */}
                  <TabsContent value="nodate" className="space-y-4">
                    {/* Recommendation Card */}
                    <Card className="bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200">
                      <CardContent className="py-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-full bg-slate-100">
                            <Calendar className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">Cómo mejorar Planning</p>
                            <ul className="text-xs text-slate-700 mt-2 space-y-1 list-disc list-inside">
                              <li>Asigna un due date estimado a cada tarea al crearla</li>
                              <li>Si no hay certeza, usa una fecha tentativa y ajústala después</li>
                              <li>Tareas sin fecha no entran en reportes de capacidad del equipo</li>
                              <li>Coordina con tu PM para priorizar y asignar fechas en batch</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Task List */}
                    <div className="overflow-y-auto max-h-[40vh] space-y-2 pr-2">
                      {dhiDiagnostic.offenders.noDate.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                          <p>¡Todas las tareas tienen fecha! 🎉</p>
                        </div>
                      ) : (
                        dhiDiagnostic.offenders.noDate.map((task, idx) => (
                          <div key={`${task.task_id}-${idx}`} className="p-3 rounded-lg border border-slate-200 bg-slate-50/50 hover:bg-slate-50">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {task.url ? (
                                  <a href={task.url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-blue-600 hover:underline text-sm">
                                    {task.task_name}
                                  </a>
                                ) : (
                                  <p className="font-medium text-slate-900 text-sm">{task.task_name}</p>
                                )}
                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                  <Badge variant="outline" className="text-xs">{task.status_status}</Badge>
                                  <span>⏱️ {Math.round(task.task_age_days || 0)} días activo</span>
                                  {task.space_name && <span className="text-slate-400">📁 {task.space_name}</span>}
                                </div>
                              </div>
                              {task.url && (
                                <a href={task.url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-slate-100 rounded">
                                  <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>

                  {/* Tab: Zombie Tasks - Afectan Flow */}
                  <TabsContent value="zombies" className="space-y-4">
                    {/* Recommendation Card */}
                    <Card className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
                      <CardContent className="py-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-full bg-purple-100">
                            <Skull className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-purple-800 text-sm">Cómo mejorar Flow</p>
                            <ul className="text-xs text-purple-700 mt-2 space-y-1 list-disc list-inside">
                              <li>Revisa tareas estancadas en 1:1s semanales</li>
                              <li>Si la tarea ya no es relevante, ciérrala o archívala</li>
                              <li>Divide tareas grandes en subtareas más manejables</li>
                              <li>Identifica bloqueos y escálalos a tu manager o PM</li>
                              <li>Considera si necesitas ayuda de otro equipo para avanzar</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Task List */}
                    <div className="overflow-y-auto max-h-[40vh] space-y-2 pr-2">
                      {dhiDiagnostic.offenders.zombies.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                          <p>¡Sin tareas estancadas! 🎉</p>
                        </div>
                      ) : (
                        dhiDiagnostic.offenders.zombies.map((task, idx) => (
                          <div key={`${task.task_id}-${idx}`} className="p-3 rounded-lg border border-purple-200 bg-purple-50/50 hover:bg-purple-50">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {task.url ? (
                                  <a href={task.url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-blue-600 hover:underline text-sm">
                                    {task.task_name}
                                  </a>
                                ) : (
                                  <p className="font-medium text-slate-900 text-sm">{task.task_name}</p>
                                )}
                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                  <Badge variant="outline" className="text-xs">{task.status_status}</Badge>
                                  <span className="text-purple-600 font-medium">⏱️ {Math.round(task.task_age_days || 0)} días activo</span>
                                  {task.space_name && <span className="text-slate-400">📁 {task.space_name}</span>}
                                </div>
                              </div>
                              {task.url && (
                                <a href={task.url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-purple-100 rounded">
                                  <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating Help Button */}
      <WorkloadHelpButton lastClickupSync={cacheInfo?.last_clickup_sync} />

      {/* Indicators Help Modal */}
      <Dialog open={showIndicatorsHelp} onOpenChange={setShowIndicatorsHelp}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <HelpCircle className="h-6 w-6 text-blue-500" />
              Guía de Indicadores - Vista Táctica
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Intro */}
            <p className="text-slate-600 text-sm">
              Esta tabla muestra indicadores de inteligencia operativa diseñados para identificar riesgos 
              y oportunidades de mejora en tiempo real. Cada indicador tiene un propósito específico.
            </p>

            {/* Hero Syndrome */}
            <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/50">
              <div className="flex items-start gap-3">
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center ring-2 ring-yellow-400">
                    <span className="text-sm font-bold text-white">H</span>
                  </div>
                  <Crown className="h-5 w-5 text-yellow-500 absolute -top-2 -right-1" fill="#eab308" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                    🦸 Hero Syndrome (Concentración de Riesgo)
                  </h3>
                  <p className="text-sm text-amber-700 mt-1">
                    <strong>¿Qué detecta?</strong> Identifica al miembro del equipo que concentra la mayor 
                    cantidad de tareas de alta prioridad (Urgent/High), convirtiéndose en un punto único de fallo.
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    <strong>Cálculo:</strong> Se muestra la corona 👑 al usuario con el mayor porcentaje 
                    de tareas críticas del sprint.
                  </p>
                  <p className="text-xs text-amber-600 mt-2 italic">
                    💡 Tip: Redistribuye tareas críticas para reducir el riesgo de dependencia en una sola persona.
                  </p>
                </div>
              </div>
            </div>

            {/* Stale Tasks */}
            <div className="p-4 rounded-lg border border-purple-200 bg-purple-50/50">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-purple-100 flex-shrink-0">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-800">
                    🕐 Tareas Estancadas (Staleness)
                  </h3>
                  <p className="text-sm text-purple-700 mt-1">
                    <strong>¿Qué detecta?</strong> Tareas en estado &quot;In Progress&quot; que no han tenido 
                    actualizaciones en más de 3 días. Indica posibles bloqueos o tareas olvidadas.
                  </p>
                  <p className="text-sm text-purple-700 mt-2">
                    <strong>Cálculo:</strong> Días transcurridos desde la última actualización (updated_date) 
                    para tareas activas. Si &gt; 3 días = Stale.
                  </p>
                  <p className="text-xs text-purple-600 mt-2 italic">
                    💡 Tip: Revisa estas tareas en los standups diarios para identificar bloqueos.
                  </p>
                </div>
              </div>
            </div>

            {/* Priority Inversion */}
            <div className="p-4 rounded-lg border border-orange-200 bg-orange-50/50">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-orange-100 flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-800">
                    ⚠️ Prioridad Invertida (Cherry Picking)
                  </h3>
                  <p className="text-sm text-orange-700 mt-1">
                    <strong>¿Qué detecta?</strong> Usuarios trabajando en tareas de baja prioridad (Normal/Low) 
                    mientras tienen tareas de alta prioridad (Urgent/High) pendientes.
                  </p>
                  <p className="text-sm text-orange-700 mt-2">
                    <strong>Cálculo:</strong> Se activa cuando: (tareas Low/Normal &quot;In Progress&quot;) &gt; 0 
                    AND (tareas Urgent/High en &quot;To Do&quot;) &gt; 0.
                  </p>
                  <p className="text-xs text-orange-600 mt-2 italic">
                    💡 Tip: Verifica si hay bloqueos en las tareas críticas o si es necesario repriorizar.
                  </p>
                </div>
              </div>
            </div>

            {/* Focus Dispersion */}
            <div className="p-4 rounded-lg border border-blue-200 bg-blue-50/50">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-blue-100 flex-shrink-0">
                  <Shuffle className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-800">
                    🎯 Dispersión de Foco (Context Switching)
                  </h3>
                  <p className="text-sm text-blue-700 mt-1">
                    <strong>¿Qué detecta?</strong> Cuántos proyectos (Spaces) diferentes tiene asignados cada 
                    usuario. Alto cambio de contexto reduce la productividad.
                  </p>
                  <p className="text-sm text-blue-700 mt-2">
                    <strong>Cálculo:</strong> Cuenta de Spaces únicos por usuario.
                    <br />• <Badge className="bg-blue-100 text-blue-700 text-xs ml-1">Enfocado</Badge> = ≤ 2 proyectos
                    <br />• <Badge className="bg-orange-100 text-orange-700 text-xs ml-1">Disperso</Badge> = &gt; 3 proyectos
                  </p>
                  <p className="text-xs text-blue-600 mt-2 italic">
                    💡 Tip: Intenta que cada persona trabaje en máximo 2-3 proyectos simultáneos.
                  </p>
                </div>
              </div>
            </div>

            {/* DHI */}
            <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-emerald-100 flex-shrink-0">
                  <Activity className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-emerald-800">
                    📊 DHI - Delivery Health Index
                  </h3>
                  <p className="text-sm text-emerald-700 mt-1">
                    <strong>¿Qué mide?</strong> Índice compuesto que evalúa la salud operativa de cada usuario 
                    basándose en tres dimensiones.
                  </p>
                  <p className="text-sm text-emerald-700 mt-2">
                    <strong>Cálculo:</strong>
                    <br />• <strong>Reliability (40%)</strong>: % de tareas NO vencidas
                    <br />• <strong>Planning (30%)</strong>: % de tareas CON fecha asignada
                    <br />• <strong>Flow (30%)</strong>: % de tareas con edad &lt; 30 días
                  </p>
                  <p className="text-sm text-emerald-700 mt-2">
                    <strong>Colores:</strong>
                    <br />• <Badge className="bg-emerald-100 text-emerald-700 text-xs">≥ 90%</Badge> Excelente
                    <br />• <Badge className="bg-amber-100 text-amber-700 text-xs">75-89%</Badge> Atención
                    <br />• <Badge className="bg-red-100 text-red-700 text-xs">&lt; 75%</Badge> Crítico
                  </p>
                  <p className="text-xs text-emerald-600 mt-2 italic">
                    💡 Tip: Haz clic en el badge DHI para ver el diagnóstico detallado con tareas ofensoras.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Pipeline Explanation */}
            <div className="p-4 rounded-lg border border-slate-300 bg-slate-50">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-slate-200 flex-shrink-0">
                  <Database className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">
                    🔄 ¿Cómo se calculan los totales?
                  </h3>
                  <p className="text-sm text-slate-700 mt-1">
                    Los datos pasan por un pipeline de filtrado antes de mostrarse en esta tabla:
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-slate-700">1</div>
                      <span className="text-slate-700"><strong>Origen:</strong> ClickUp → Fivetran → Databricks</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-slate-700">2</div>
                      <span className="text-slate-700"><strong>Filtro SQL:</strong> Solo miembros del equipo seleccionado</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-slate-700">3</div>
                      <span className="text-slate-700"><strong>Deduplicación:</strong> Se eliminan task_id duplicados</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-slate-700">4</div>
                      <span className="text-slate-700"><strong>Exclusión:</strong> Se quitan status de espera</span>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-white rounded border border-slate-200">
                    <p className="text-xs text-slate-600 font-medium mb-2">Status excluidos automáticamente:</p>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline" className="text-xs">ready for review</Badge>
                      <Badge variant="outline" className="text-xs">waiting for feedback</Badge>
                      <Badge variant="outline" className="text-xs">waiting for review</Badge>
                      <Badge variant="outline" className="text-xs">send ok to enovia</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3 italic">
                    📌 Por esto, el &quot;total&quot; en Hero Syndrome refleja solo las tareas críticas <strong>activas</strong> del sprint, 
                    no el total bruto de ClickUp. Esto asegura que los porcentajes sean relevantes para el trabajo actual.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                Estos indicadores se calculan en tiempo real basándose en los datos de ClickUp sincronizados vía Fivetran.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
